import { useMESStore } from '@/stores/useMESStore';
import { randomFrom } from '@/agent/utils';
import type { UIAction, AgentMemory, AgentWidget, Message } from '@/agent/agentStore';
import { findFlowTrigger, startFlow, advanceFlow } from '@/agent/flows';
import { newIdempotencyKey } from '@/agent/sessionId';
import { resolveWorkOrder } from '@/lib/woResolver';

// ============================================================
// Types
// ============================================================
export interface IntentResponse {
  reply: string | string[];
  uiActions?: UIAction[];
  widgets?: AgentWidget[];
  memoryPatch?: Partial<AgentMemory>;
  followUp?: string;
  intentId?: string;
}

interface Intent {
  id: string;
  patterns: RegExp[];
  handler: (match: RegExpMatchArray, message: string, memory: AgentMemory) => IntentResponse;
}

// ============================================================
// Helpers
// ============================================================
export function extractWOId(message: string): string | null {
  const m = message.match(/WO-\d{4}(?:-\d{4})?/i);
  return m ? m[0].toUpperCase() : null;
}

export function extractNumber(message: string): number | null {
  const m = message.match(/-?\d+(?:[.,]\d+)?/);
  if (!m) return null;
  return Number(m[0].replace(',', '.'));
}

const fakePercent = (min: number, max: number) =>
  (Math.random() * (max - min) + min).toFixed(1);

// ============================================================
// Entity extraction (pre-intent)
// ============================================================
const ENTITY_PATTERNS: Array<{ key: string; regex: RegExp }> = [
  { key: 'wo', regex: /WO-\d{4}(?:-\d{4})?/i },
  { key: 'serial', regex: /SN-\d{4,}/i },
  { key: 'sscc', regex: /SSCC-\d{4,}/i },
  { key: 'plant', regex: /\b(?:MDC|MAD)-\d{2}\b/i },
];

export function extractEntities(message: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const { key, regex } of ENTITY_PATTERNS) {
    const m = message.match(regex);
    if (m) out[key] = m[0].toUpperCase();
  }
  return out;
}


// ============================================================
// Intents — order matters: specific first, fallback last
// ============================================================
const intents: Intent[] = [
  // ============================================================
  // A. MEMORY-AWARE (deben ir antes que los genéricos)
  // ============================================================
  {
    id: 'recap',
    patterns: [/\b(qué\s+est[áa]bamos|que\s+estabamos|recu[ée]rdame|qué\s+est[áa]bamos\s+viendo|on\s+what\s+were\s+we)\b/i],
    handler: (_m, _msg, memory) => ({ reply: getConversationSummary(memory, []) }),
  },
  {
    id: 'followup_trace',
    patterns: [/^\s*(y\s+(el\s+)?(lote|trazabilidad|traza|genealog))/i],
    handler: (_m, _msg, memory) => {
      const wo = memory.lastWorkOrder;
      if (!wo) return { reply: 'No sé a qué orden te refieres. Prueba a decir "cambiar a WO-2024-0001".' };
      return {
        reply: `Trazando ${wo}…`,
        uiActions: [
          { type: 'navigate', to: '/traceability/search' },
          { type: 'set_search', view: 'traceability', value: wo },
        ],
      };
    },
  },
  {
    id: 'followup_call_responsible',
    patterns: [/\bavisa\s+(al\s+)?responsable\b/i],
    handler: (_m, _msg, memory) => {
      const wo = memory.lastWorkOrder ?? 'la orden activa';
      return {
        reply: `Aviso lanzado para ${wo}. El supervisor recibirá notificación en 2 min.`,
        uiActions: [{ type: 'toast', message: `Aviso enviado · ${wo}`, level: 'success' }],
      };
    },
  },
  {
    id: 'pronoun_wo',
    patterns: [
      /\b(esa\s+orden|esa\s+wo|la\s+[úu]ltima\s+orden|la\s+que\s+(acabo|acababa)\s+de\s+abrir|lo\s+que\s+estaba\s+viendo|that\s+order)\b/i,
      /\b(mu[ée]strame|abre|open|show|detalle\s+de)\s+(esa|la\s+misma|the\s+same)\b/i,
    ],
    handler: (_m, _msg, memory) => {
      const wo = memory.lastWorkOrder;
      if (!wo) return { reply: "No sé a qué orden te refieres. Prueba a decir 'muéstrame la WO-2024-0892'." };
      return {
        reply: `Abriendo detalle de ${wo}…`,
        uiActions: [
          { type: 'navigate', to: '/production/dispatch' },
          { type: 'select_item', view: 'dispatch_board', id: wo },
          { type: 'highlight', selector: `[data-id="${wo}"]`, durationMs: 2500 },
        ],
      };
    },
  },

  // ============================================================
  // A-bis. CAMERA ENTITY DRILL-DOWN (L1 · 100-interfaces roadmap)
  //         Order: BEFORE generic open_calibration / open_devices.
  //         All require an ID like CAM-01 to keep specificity high.
  // ============================================================
  {
    id: 'devices_open_camera_kpis',
    patterns: [
      /\b(?:kpis?|m[eé]tricas?|metrics|rendimiento|performance|tasa\s+(?:de\s+)?detecci[oó]n|detection\s+rate|estad[íi]sticas?|stats?\b|throughput)\b[^\n]*\b(?:c[áa]mara|camera)\b[^\n]*?(CAM-\d+|\d{1,2})\b/i,
      // L9.5 Bloque 2.5 (audit fix): \b después de stats? para no matchear "status".
      /\b(?:c[áa]mara|camera)\b[^\n]*?(CAM-\d+|\d{1,2})\b[^\n]*\b(?:kpis?|m[eé]tricas?|metrics|rendimiento|performance|tasa\s+detecci|detection\s+rate|stats?\b|estad[íi]sticas?|throughput)/i,
      /\b(?:m[eé]tricas?|metrics|rendimiento|performance|tasa\s+detecci|detection\s+rate)\b\s+(?:de\s+)?(CAM-\d+)/i,
      // L9.5 Bloque 2.5 · "throughput cámara X últimas 24h" / "camera X throughput last 24h"
      /\bthroughput\s+(?:c[áa]mara|camera)\s+(CAM-\d+|\d{1,2})/i,
    ],
    handler: (m) => {
      const raw = (m[1] ?? '').toUpperCase();
      const id = raw.startsWith('CAM-') ? raw : `CAM-${String(raw).padStart(2, '0')}`;
      return {
        reply: `KPIs cámara ${id}`,
        uiActions: [{ type: 'navigate', to: `/backoffice/devices/cameras/${id}/kpis` }],
      };
    },
  },
  {
    id: 'devices_open_camera_calibration',
    patterns: [
      // ES — verbo/sustantivo de calibración + cámara
      /\b(?:calibraci[oó]n(?:es)?|calibration|calibrar|calibrate|matriz\s+intr[íi]nseca|intrinsic\s+matrix|par[áa]metros\s+[óo]pticos|optical\s+parameters|error\s+de\s+reproyecci[oó]n|reprojection\s+error|c[oó]mo\s+est[áa]\s+calibrada|how\s+is\s+(?:the\s+)?camera[^\n]*calibrated|hist[oó]rico\s+de\s+calibraci|calibration\s+history)\b[^\n]*\b(?:c[áa]mara|camera)\s+([A-Z]{2,4}-\d+|\d{1,2})/i,
      /\b(?:c[áa]mara|camera)\s+([A-Z]{2,4}-\d+|\d{1,2})\s+(?:calibraci[oó]n|calibration|calibrada|calibrated)/i,
      // L9.5 Bloque 2.5 · sin "cámara" delante: "matriz intrínseca de CAM-X", "optical params CAM-X", "cómo está calibrada CAM-X"
      /\b(?:matriz\s+intr[íi]nseca|intrinsic\s+matrix|optical\s+param(?:eter)?s?|par[áa]metros\s+[óo]pticos|reprojection\s+error|error\s+de\s+reproyecci[oó]n)\s+(?:de\s+)?(CAM-\d+)/i,
      /\bc[oó]mo\s+est[áa]\s+calibrada\s+(CAM-\d+)/i,
      /\bhow\s+is\s+(CAM-\d+)\s+calibrated/i,
    ],
    handler: (m) => {
      const raw = m[1] ?? '';
      const id = raw.toUpperCase().includes('CAM-')
        ? raw.toUpperCase()
        : `CAM-${String(raw).padStart(2, '0')}`;
      return {
        reply: `Calibración de ${id}`,
        uiActions: [{ type: 'navigate', to: `/backoffice/devices/cameras/${id}/calibration` }],
      };
    },
  },
  {
    id: 'devices_open_camera_live',
    patterns: [
      /\b(?:live|stream|en\s+vivo|directo|video\s+en\s+directo|live\s+feed|live\s+view|watch|qu[eé]\s+est[áa]\s+viendo|what'?s\s+camera[^\n]*seeing)\b[^\n]*\b(?:c[áa]mara|camera)\b[^\n]*?(CAM-\d+|\d{1,2})\b/i,
      /\b(?:c[áa]mara|camera)\b[^\n]*?(CAM-\d+|\d{1,2})\b[^\n]*\b(?:en\s+vivo|live|stream|directo|seeing|viendo)/i,
      /\b(?:live|stream)\b\s+(?:de\s+)?(CAM-\d+)/i,
    ],
    handler: (m) => {
      const raw = (m[1] ?? '').toUpperCase();
      const id = raw.startsWith('CAM-') ? raw : `CAM-${String(raw).padStart(2, '0')}`;
      return {
        reply: `Stream en vivo de ${id}`,
        uiActions: [{ type: 'navigate', to: `/backoffice/devices/cameras/${id}/live` }],
      };
    },
  },
  // ---- L2 — CAM-DETECT-HISTORY (multi-slot: clase, rango, conf, defectOnly) ----
  {
    id: 'devices_open_camera_history',
    // L9.5 Bloque 2.5: rango/clase/conf → set_filter (no query string).
    // Patrones añadidos: "detection history CAM-X", "what did camera X see today",
    // "defectos detectados por cámara X últimos 7 días".
    patterns: [
      /\b(?:hist[oó]rico|historial|detecciones|detections?|history|qu[eé]\s+ha\s+visto|qu[eé]\s+vio|detection\s+history)(?!\s+de\s+calibraci)\b[^\n]*\b(?:c[áa]mara|camera)\b[^\n]*?(CAM-\d+|\d{1,2})\b/i,
      /\b(?:c[áa]mara|camera)\b[^\n]*?(CAM-\d+|\d{1,2})\b[^\n]*\b(?:hist[oó]rico|historial|detecciones|detections?|history|defects?|seen|visto)\b/i,
      /\b(?:detections?|history|defects?|detection\s+history)\b[^\n]*\bcamera\s+([A-Z]{2,4}-\d+|\d{1,2})/i,
      /\bcamera\s+([A-Z]{2,4}-\d+|\d{1,2})\s+(?:detections?|history|defects?)/i,
      // L9.5 Bloque 2.5 · "what did camera X see today/yesterday/..."
      /\bwhat\s+did\s+camera\s+([A-Z]{2,4}-\d+|\d{1,2})\s+see/i,
      // L9.5 Bloque 2.5 · "detection history CAM-01" / "history CAM-X" sin "camera"
      /\b(?:detection\s+)?history\s+(CAM-\d+)/i,
      /\b(?:hist[oó]rico|historial|detecciones?)\s+(?:de\s+)?(CAM-\d+)/i,
      // L9.5 Bloque 2.5 · "defectos detectados por cámara X / defects detected by camera X"
      /\b(?:defectos?|defects?)\s+detectad[oa]s?\s+(?:por|by)\s+(?:la\s+)?(?:c[áa]mara|camera)\s+(CAM-\d+|\d{1,2})/i,
      // Slot-first: "scratch ayer cámara 3 ...", "porosidad cámara 5 mes", "dent camera 5 last month"
      /\b(scratch|dent|porosity|porosidad|misalign|burn|oil-stain|ara[ñn]azo|abolladura|quemadura|aceite|desalineado)\b[^\n]*\b(?:c[áa]mara|camera)\s+([A-Z]{2,4}-\d+|\d{1,2})/i,
      // L9.5 Bloque 2.5 · "dent CAM-05 último mes" — slot directo + CAM-XX (sin "cámara")
      /\b(scratch|dent|porosity|porosidad|misalign|burn|oil-stain|ara[ñn]azo|abolladura|quemadura|aceite|desalineado)\s+(CAM-\d+)/i,
    ],
    handler: (m, msg) => {
      // L9.5 Bloque 2.5 (audit fix): siempre buscar CAM-XX o "cámara N" en el msg primero,
      // para evitar que un grupo de captura "dent"/"scratch" se interprete como id.
      let id: string;
      const explicit = msg.match(/CAM-(\d+)/i);
      if (explicit) {
        id = `CAM-${explicit[1].padStart(2, '0')}`;
      } else {
        const near = msg.match(/(?:c[áa]mara|camera)\s+(\d{1,2})/i);
        const raw = near?.[1] ?? (m[1] && /^\d{1,2}$/.test(m[1]) ? m[1] : (m[2] && /^\d{1,2}$/.test(m[2]) ? m[2] : '01'));
        id = `CAM-${String(raw).padStart(2, '0')}`;
      }

      // Slot extraction
      const text = msg.toLowerCase();
      const classMap: Record<string, string> = {
        scratch: 'scratch', 'arañazo': 'scratch', 'aranazo': 'scratch',
        dent: 'dent', 'abolladura': 'dent',
        porosity: 'porosity', 'porosidad': 'porosity',
        misalign: 'misalign', 'desalineado': 'misalign', 'desalineacion': 'misalign',
        burn: 'burn', 'quemadura': 'burn',
        'oil-stain': 'oil-stain', 'aceite': 'oil-stain',
      };
      let klass: string | null = null;
      for (const [k, v] of Object.entries(classMap)) {
        if (text.includes(k)) { klass = v; break; }
      }

      let range: string | null = null;
      if (/\b(hoy|today)\b/.test(text)) range = '24h';
      else if (/\b(ayer|yesterday|last\s*24)\b/.test(text)) range = '24h';
      else if (/\b(semana|week|7\s*d[íi]as|7\s*days)\b/.test(text)) range = '7d';
      else if (/\b(mes|month|30\s*d[íi]as|30\s*days)\b/.test(text)) range = '30d';
      else if (/\b(hora|hour|1h)\b/.test(text)) range = '1h';
      else if (/\b(todo|all)\b/.test(text)) range = 'all';

      const confMatch = msg.match(/(?:conf(?:ianza|idence)?\s*[>≥]?\s*)(\d*\.?\d+)/i);
      const conf = confMatch ? Math.min(1, Number(confMatch[1])) : null;

      const defectOnly = /\b(defectos?|defects?|solo\s+defectos|defect\s*only)\b/i.test(msg);

      // L9.5 Bloque 2.5: canonical path sin query strings; slots como set_filter UI-actions.
      const setFilters: UIAction[] = [];
      if (klass) setFilters.push({ type: 'set_filter', view: 'camera-history', field: 'class', value: klass });
      if (range) setFilters.push({ type: 'set_filter', view: 'camera-history', field: 'range', value: range });
      if (conf !== null) setFilters.push({ type: 'set_filter', view: 'camera-history', field: 'conf', value: conf });
      if (defectOnly) setFilters.push({ type: 'set_filter', view: 'camera-history', field: 'defectOnly', value: true });

      const path = `/backoffice/devices/cameras/${id}/detections`;
      const slotsText = [
        klass ? `clase ${klass}` : null,
        range ? `rango ${range}` : null,
        conf !== null ? `conf ≥ ${conf}` : null,
        defectOnly ? 'solo defectos' : null,
      ].filter(Boolean).join(' · ');

      return {
        reply: slotsText
          ? `Histórico ${id} · ${slotsText}`
          : `Histórico de detecciones ${id}`,
        uiActions: [
          { type: 'navigate', to: path },
          ...setFilters,
        ],
      };
    },
  },
  // ---- L2 — CAM-CONFIG (read-only, optional focus on section) ----
  {
    id: 'devices_open_camera_config',
    patterns: [
      /\b(?:configuraci[oó]n|config|settings|ajustes|par[áa]metros)\b[^\n]*\b(?:c[áa]mara|camera)\b[^\n]*?(CAM-\d+|\d{1,2})\b/i,
      /\b(?:c[áa]mara|camera)\b[^\n]*?(CAM-\d+|\d{1,2})\b[^\n]*\b(?:configuraci[oó]n|config|settings|ajustes|exposure|exposici[oó]n|model|roi)/i,
      /\b(?:roi|exposure|exposici[oó]n|(?:ai\s+)?model(?:o)?(?:\s+(?:asignado|de\s+ia|assigned))?|alert\s*rules?|reglas?\s+de\s+alerta|qu[ée]\s+modelo)\b[^\n]*\b(?:c[áa]mara|camera)\b[^\n]*?(CAM-\d+|\d{1,2})\b/i,
      /\b(?:roi|exposure|exposici[oó]n)\b\s+(CAM-\d+)/i,
      /\b(CAM-\d+)\s+(?:roi|exposure|exposici[oó]n)\b/i,
    ],
    handler: (m, msg) => {
      const raw = m[1] ?? m[2] ?? '';
      const id = raw.toUpperCase().includes('CAM-')
        ? raw.toUpperCase()
        : `CAM-${String(raw).padStart(2, '0')}`;

      const text = msg.toLowerCase();
      let focus: string | null = null;
      if (/\broi\b/.test(text)) focus = 'section-roi';
      else if (/exposici[oó]n|exposure|sensor|gain|gamma|white\s*balance/.test(text)) focus = 'section-sensor';
      else if (/modelo|model|inferencia|inference|umbral|threshold|gpu|npu/.test(text)) focus = 'section-inference';
      else if (/alert|regla|rule/.test(text)) focus = 'section-alerts';
      else if (/fps|resoluci[oó]n|resolution|trigger|bitrate|captura|capture/.test(text)) focus = 'section-capture';

      const actions: UIAction[] = [
        { type: 'navigate', to: `/backoffice/devices/cameras/${id}/config` },
      ];
      if (focus) {
        actions.push({ type: 'highlight', selector: focus, durationMs: 1500 });
      }
      return {
        reply: focus
          ? `Configuración ${id} · ${focus.replace('section-', '')}`
          : `Configuración de ${id}`,
        uiActions: actions,
      };
    },
  },
  // ---- L3 — CAM-HEALTH (diagnóstico técnico, alertas activas) ----
  {
    id: 'devices_open_camera_health',
    patterns: [
      // L9.5 Bloque 2.5 (audit fix): "camera N status" → detail (catálogo TR-007),
      // "CAM-NN status" → health (catálogo TR-047). Solo health acepta "CAM-NN status";
      // "camera N status" lo gestiona detail.
      /\b(?:salud|diagnóstico|diagnostico|diagnose|estado\s+t[ée]cnico|qu[ée]\s+problemas?\s+tiene|est[áa]\s+bien|issues?|problemas?)\b[^\n]*\b(?:c[áa]mara|camera)\s+([A-Z]{2,4}-\d+|\d{1,2})/i,
      /\b(?:c[áa]mara|camera)\s+([A-Z]{2,4}-\d+|\d{1,2})\s+(?:salud|diagn[oó]stico|estado\s+t[ée]cnico|alertas|alerts|health|healthy|issues?|problemas?)\b/i,
      /\balertas?\b[^\n]*\b(?:c[áa]mara|camera)\s+([A-Z]{2,4}-\d+|\d{1,2})/i,
      /\b(?:health|diagnostic|diagnose|alerts|status|issues?)\b[^\n]*\bcamera\s+([A-Z]{2,4}-\d+|\d{1,2})/i,
      /\bis\s+camera\s+([A-Z]{2,4}-\d+|\d{1,2})\s+(?:healthy|ok|fine)\b/i,
      // L9.5 Bloque 2.5 · "CAM-06 status" / "CAM-04 health" / "issues CAM-X" sin "camera"
      /\b(CAM-\d+)\s+(?:status|health|healthy|issues?|problemas?|alerts?)\b/i,
      /\b(?:issues?|problemas?|status|health)\s+(CAM-\d+)/i,
    ],
    handler: (m) => {
      const raw = m[1] ?? '';
      const id = raw.toUpperCase().includes('CAM-')
        ? raw.toUpperCase()
        : `CAM-${String(raw).padStart(2, '0')}`;
      // Compute health snapshot for richer toast
      let summary = '';
      try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const { generateCameraHealth } = require('@/data/generators/cameraHealthGenerator');
        const h = generateCameraHealth(id);
        if (h.overallStatus !== 'ok' && h.alerts[0]) {
          summary = ` · ${h.overallStatus.toUpperCase()}: ${h.alerts[0].message}`;
        } else {
          summary = ` · estado ${h.overallStatus}`;
        }
      } catch {
        // ignore
      }
      return {
        reply: `Salud de ${id}${summary}`,
        uiActions: [
          { type: 'navigate', to: `/backoffice/devices/cameras/${id}/health` },
          { type: 'highlight', selector: '[data-section="alerts-active"]', durationMs: 2000 },
        ],
      };
    },
  },
  // ---- L3 — CAM-COMPARE (multi-slot list of camera IDs, 2-4) ----
  {
    id: 'devices_open_camera_compare',
    patterns: [
      /\b(?:comparar?|comparativa|compara|compare|side[\s-]?by[\s-]?side|diferencias?(?:\s+entre)?|differences?|vs\.?|qu[ée]\s+c[áa]maras?\s+va(?:n)?\s+mejor)\b[^\n]*\b(?:c[áa]maras?|cameras?)\b/i,
      /\b(?:c[áa]maras?|cameras?)\b[^\n]*\bvs\b[^\n]*\b(?:c[áa]maras?|cameras?|[A-Z]{2,4}-\d+)/i,
      /\bCAM-\d+\s*(?:vs\.?|y|and|,)\s*CAM-\d+/i,
      /CAM-\d+[^\n]{0,30}CAM-\d+/i,
      /\bqu[ée]\s+c[áa]maras?\s+va(?:n)?\s+mejor\s+entre\s+\d/i,
      // L9.5 Bloque 2.5 · "which works best 5 vs 6" / "which is better 5 vs 6"
      /\bwhich\s+(?:works\s+best|is\s+better|performs\s+best)\s+\d{1,2}\s*vs\.?\s*\d{1,2}/i,
      /\bcu[áa]l\s+va\s+mejor\s+\d{1,2}\s*vs\.?\s*\d{1,2}/i,
    ],
    handler: (_m, msg) => {
      const text = msg.toLowerCase();
      // 1) Explicit CAM-XX ids
      const explicit = Array.from(msg.matchAll(/CAM-\d+/gi)).map((x) => x[0].toUpperCase());
      const ids = new Set<string>(explicit);

      // 2) Bare numbers after "cámaras"/"cameras" — multi-slot list
      if (ids.size < 2) {
        const seg = msg.match(/(?:c[áa]maras?|cameras?)[^\n]*?((?:\d{1,2}[\s,yand]+){0,3}\d{1,2})/i);
        if (seg) {
          const nums = (seg[1].match(/\d{1,2}/g) ?? []).slice(0, 4);
          for (const n of nums) ids.add(`CAM-${n.padStart(2, '0')}`);
        }
      }
      // L9.5 Bloque 2.5 · "which works best 5 vs 6" / "cuál va mejor 5 vs 6": números sin keyword "cámaras"
      if (ids.size < 2 && /\bvs\.?\b/i.test(msg)) {
        const nums = msg.match(/\b(\d{1,2})\s*vs\.?\s*(\d{1,2})\b/i);
        if (nums) {
          ids.add(`CAM-${nums[1].padStart(2, '0')}`);
          ids.add(`CAM-${nums[2].padStart(2, '0')}`);
        }
      }

      // 3) Resolution by line ("línea A" / "line A") — pick all cams on that line.
      // L9.5 Bloque 2.5 (audit fix): catálogo expande "línea A" a 4 cámaras (CAM-01..04).
      if (ids.size < 2 && /\b(l[íi]nea|line)\s+([a-d])/i.test(text)) {
        const lineMatch = text.match(/\b(?:l[íi]nea|line)\s+([a-d])/i);
        const letter = (lineMatch?.[1] ?? '').toUpperCase();
        const map: Record<string, string[]> = {
          A: ['CAM-01', 'CAM-02', 'CAM-03', 'CAM-04'],
          B: ['CAM-03', 'CAM-04', 'CAM-05', 'CAM-06'],
          C: ['CAM-05', 'CAM-06'],
        };
        for (const c of map[letter] ?? []) ids.add(c);
      }

      const list = Array.from(ids).slice(0, 4);
      if (list.length < 2) {
        return {
          reply: 'Para comparar necesito al menos 2 cámaras. Dime los IDs (p. ej. "comparar CAM-01 vs CAM-02").',
        };
      }
      const qs = `ids=${list.join(',')}`;
      return {
        reply: `Comparativa side-by-side · ${list.join(' · ')}`,
        uiActions: [
          { type: 'navigate', to: `/backoffice/devices/cameras/compare?${qs}` },
        ],
      };
    },
  },
  // ============================================================
  // L6 — M2 Work Orders · Core (apertura M2 — deben preceder a L5/L4)
  // ============================================================
  // ---- L6 — WO-TIMELINE (más específico que WO-DETAIL: debe ir antes) ----
  // L9.5 · expanded patterns to cover catalog colloquialisms.
  {
    id: 'production_open_wo_timeline',
    patterns: [
      /\b(?:timeline|cronolog[íi]a|history|historial|hist[oó]rico)\b.*\b(?:WO|orden|order)/i,
      /\b(?:WO|orden|order)[-\s]?\d.*\b(?:timeline|cronolog[íi]a|history|historial)\b/i,
      // events / eventos requires WO/orden token to avoid catching generic "event"
      /\b(?:events?|eventos)\b.*\b(?:WO|orden|order)[-\s]?\d/i,
      /\b(?:WO|orden|order)[-\s]?\d.*\b(?:events?|eventos)\b/i,
      // L9.5 · "histórico de eventos de la orden N" / "event history order N"
      /\bhist[oó]rico\s+de\s+eventos?\b/i,
      /\bevent\s+history\b/i,
      // L9.5 · "qué ha pasado en/con WO-X" → timeline (event narrative).
      // Note: bare "qué pasó" goes to variance (root-cause), not timeline.
      /\bqu[ée]\s+ha\s+pasado\s+(?:en|con)\s+(?:la\s+)?(?:WO|orden|order)/i,
      // L9.5 Bloque 2.5 (audit fix): catálogo TR para "what happened WO-X" → timeline.
      /\bwhat\s+happened\s+(?:in|on|with)?\s*(?:the\s+)?(?:WO|order)[-\s]?\d/i,
      // L9.5 · "actividad operario op-XXX en WO-Y" / "op-NNN activity WO-Y" → timeline view
      /\bactividad\s+operari[oa]\b/i,
      /\boperator\s+activity\b/i,
      /\bop-?\d+\s+activity\b/i,
      // L9.5 · "order N chronology"
      /\b(?:WO|orden|order)\s+\d+\s+chronology\b/i,
      // L9.5 · "NCs en el timeline / NCs in timeline" / "eventos críticos / critical events"
      /\beventos?\s+cr[íi]ticos?\b/i,
      /\bcritical\s+events?\b/i,
    ],
    handler: (_m, msg) => {
      const r = resolveWorkOrder(msg);
      if (!r.wo) return { reply: 'No identifico la WO. Prueba "timeline de WO-2026-0042".' };
      const range = /24\s?h/i.test(msg) ? '24h' : /8\s?h/i.test(msg) ? '8h' : 'all';
      return {
        intentId: 'production_open_wo_timeline',
        reply: `Abriendo cronología de ${r.wo.id} (${r.wo.customerName}).`,
        uiActions: [
          { type: 'navigate', to: `/production/work-orders/${r.displayId ?? r.wo.id}/timeline` },
          { type: 'toast', level: 'info', text: `${r.wo.id} timeline (rango ${range}).` },
        ],
      };
    },
  },
  // ---- L6 — WO-BOM ----
  {
    id: 'production_open_wo_bom',
    patterns: [
      /\b(?:BOM|lista\s+de\s+materiales|materials?|materiales?|componentes?|components?|consumo\s+material|estructura\s+producto|backorder|flat\s+list|missing\s+items?|items?\s+faltantes?)\b.*\b(?:WO|orden|order)/i,
      /\b(?:WO|orden|order)[-\s]?\d.*\b(?:BOM|materiales?|materials?|componentes?|components?|backorder|consumo|flat\s+list|missing\s+items?|items?\s+faltantes?)/i,
      /\bmissing\s+items?\s+WO/i,
      /\bqu[ée]\s+materiales?\s+(?:lleva|tiene|usa|consume)\b/i,
      /\bwhat\s+materials?\s+(?:does|do)\b/i,
    ],
    handler: (_m, msg) => {
      const r = resolveWorkOrder(msg);
      if (!r.wo) return { reply: 'No identifico la WO para mostrar su BOM.' };
      const filter = /backorder/i.test(msg) ? 'backorder' : undefined;
      return {
        intentId: 'production_open_wo_bom',
        reply: `Abriendo BOM de ${r.wo.id}.`,
        uiActions: [
          { type: 'navigate', to: `/production/work-orders/${r.displayId ?? r.wo.id}/bom${filter ? '?status=backorder' : ''}` },
          { type: 'toast', level: filter ? 'warning' : 'info', text: filter ? `${r.wo.id}: filtrando items en backorder.` : `BOM de ${r.wo.id} cargada.` },
        ],
      };
    },
  },
  // ============================================================
  // ---- L7 — WO-ROUTING (operations/Gantt) — must precede DETAIL ----
  {
    id: 'production_open_wo_routing',
    patterns: [
      // L9.5 · explicit guard: "operaciones saturadas/saturated ops" → bottleneck, not routing
      /\b(?:routing|ruta|gantt|secuencia|steps?|pasos|workflow|tiempos?\s+operaci[oó]n|op\s+times?|pending\s+ops?|operaciones?\s+pendientes?)\b.*\b(?:WO|orden|order)/i,
      /\b(?:WO|orden|order)[-\s]?\d.*\b(?:routing|ruta|gantt|steps?|pasos|tiempos?\s+operaci[oó]n|op\s+times?|pending\s+ops?)/i,
      // Bare "operaciones?/operations?" only when NOT followed by saturad*/saturated (those go to bottleneck)
      /\b(?:operaciones?|operations?)\b(?!\s+(?:saturad|saturated)).*\b(?:WO|orden|order)/i,
      /\b(?:WO|orden|order)[-\s]?\d.*\b(?:operaciones?|operations?)\b(?!\s+(?:saturad|saturated))/i,
      /\bdiagrama\s+gantt\b/i,
      // L9.5 · "operaciones de la orden N" / "order N operations"
      /\boperaciones?\s+de\s+(?:la\s+)?(?:WO|orden|order)\s+\d/i,
      /\b(?:WO|orden|order)\s+\d+\s+operations?\b/i,
    ],
    handler: (_m, msg) => {
      const r = resolveWorkOrder(msg);
      if (!r.wo) return { reply: 'No identifico la WO. Prueba "routing de WO-2026-0042".' };
      const view = /gantt|diagrama/i.test(msg) ? 'gantt' : 'list';
      return {
        intentId: 'production_open_wo_routing',
        reply: `Abriendo routing de ${r.wo.id} (${r.wo.customerName}) en vista ${view}.`,
        uiActions: [
          { type: 'navigate', to: `/production/work-orders/${r.displayId ?? r.wo.id}/routing` },
          { type: 'toast', level: 'info', text: `${r.wo.id} routing — vista ${view}.` },
        ],
      };
    },
  },
  // ---- L7 — WO-COSTS (financial breakdown) ----
  // L9.5 · MUST precede ncs_scrap so "scrap WO-X" → costs (not quality board).
  // L9.5 · explicit guard: "varianza coste / cost variance" → variance, not costs.
  {
    id: 'production_open_wo_costs',
    patterns: [
      // Skip when "varianza/variance" appears in the message (handled by variance handler).
      /^(?!.*\b(?:varianzas?|variance)\b).*\b(?:costes?|costs?|coste\s+real|breakdown|presupuesto|budget|euros?|d[óo]lares?|usd|eur|por\s+unidad|per[-\s]?unit|scrap)\b.*\b(?:WO|orden|order)[-\s]?\d/i,
      /^(?!.*\b(?:varianzas?|variance)\b).*\b(?:WO|orden|order)[-\s]?\d.*\b(?:costes?|costs?|breakdown|presupuesto|budget|usd|eur|scrap)/i,
      // L9.5 · "cuánto cuesta la orden N" / "how much does order N cost"
      /\bcu[áa]nto\s+cuesta\s+(?:la\s+)?(?:WO|orden|order)/i,
      /\bhow\s+much\s+(?:does|did)\s+(?:WO|order)/i,
    ],
    handler: (_m, msg) => {
      const r = resolveWorkOrder(msg);
      if (!r.wo) return { reply: 'No identifico la WO. Prueba "costes de WO-2026-0042".' };
      const usd = /\busd|d[óo]lares?\b/i.test(msg);
      const perUnit = /por\s+unidad|per[-\s]?unit/i.test(msg);
      return {
        intentId: 'production_open_wo_costs',
        reply: `Abriendo costes de ${r.wo.id} en ${usd ? 'USD' : 'EUR'}${perUnit ? ' (por unidad)' : ''}.`,
        uiActions: [
          { type: 'navigate', to: `/production/work-orders/${r.displayId ?? r.wo.id}/costs` },
          { type: 'toast', level: 'info', text: `${r.wo.id} costes — Δ ${r.wo.costActual >= r.wo.costPlanned ? '+' : ''}${(((r.wo.costActual - r.wo.costPlanned)/Math.max(1,r.wo.costPlanned))*100).toFixed(1)}%.` },
        ],
      };
    },
  },
  // ---- L7 — WO-OPERATORS (assigned operators) ----
  {
    id: 'production_open_wo_operators',
    patterns: [
      /\b(?:operari[oa]s?|operators?|trabajadores?|workers?|equipo|team|productividad|defect\s+rate|top\s+performers?|watch\s+list|horas?\s+hombre|man[-\s]?hours?)\b.*\b(?:WO|orden|order)/i,
      /\b(?:WO|orden|order)[-\s]?\d.*\b(?:operari[oa]s?|operators?|productividad|equipo|team|defect|horas?\s+hombre|man[-\s]?hours?)/i,
      /\bqui[ée]n(?:es)?\s+(?:trabaj[oó]?|trabaja|est[áa]\s+trabajando|trabajaron)\s+(?:en\s+(?:la\s+)?)?(?:WO|orden|order)/i,
      // L9.5 · "who worked WO-X" / "who is on WO-X"
      /\bwho\s+(?:worked|works|is\s+(?:on|working))\s+(?:on\s+)?(?:WO|order)/i,
    ],
    handler: (_m, msg) => {
      const r = resolveWorkOrder(msg);
      if (!r.wo) return { reply: 'No identifico la WO. Prueba "operarios de WO-2026-0042".' };
      const top = /top\s+performers?/i.test(msg);
      const watch = /watch\s+list/i.test(msg);
      return {
        intentId: 'production_open_wo_operators',
        reply: `Abriendo operarios asignados a ${r.wo.id}${top ? ' — sección Top performers' : watch ? ' — sección Watch list' : ''}.`,
        uiActions: [
          { type: 'navigate', to: `/production/work-orders/${r.displayId ?? r.wo.id}/operators` },
          { type: 'toast', level: 'info', text: `Operarios de ${r.wo.id} cargados.` },
        ],
      };
    },
  },
  // ============================================================
  // L8 — WO Progreso · Docs · Certs (debe ir antes que L6 detail)
  // ============================================================
  {
    id: 'production_open_wo_progress',
    patterns: [
      /\b(?:progreso|progress|avance|curva\s*s|s[-\s]?curve|throughput|live|en\s+vivo|eta)\b.*\b(?:WO|orden|order)/i,
      /\b(?:WO|orden|order)[-\s]?\d.*\b(?:progreso|progress|avance|curva\s*s|throughput|eta|live|retrasad|delayed?)/i,
      // L9.5 · "cómo va la orden N" / "how is order N" / "está retrasada WO-X"
      /\bc[oó]mo\s+(?:va|est[áa]|avanza)\s+(?:la\s+)?(?:WO|orden|order)/i,
      /\bhow\s+is\s+(?:WO|order)\s+\d/i,
      /\b(?:est[áa]\s+retrasad|is\s+(?:WO|order)\s+\d+\s+(?:late|delayed))/i,
      // L9.5 · "ETA WO-X" alone (no other token)
      /\bETA\s+WO[-\s]?\d/i,
      // L9.5 · "throughput live WO-X" already caught by line 1 but anchor explicit
      /\bthroughput\s+live\b/i,
      // L9.5 (audit fix) · "probabilidad on-time / on-time prob" → progress (not delay_risk).
      // Per catalog these track delivery progress, not risk forecasting.
      /\bprobabilidad\s+on[-\s]?time\b/i,
      /\bon[-\s]?time\s+prob(?:ability)?\b/i,
    ],
    handler: (_m, msg) => {
      const r = resolveWorkOrder(msg);
      if (!r.wo) return { reply: 'No identifico la WO. Prueba "progreso de WO-2026-0042".' };
      return {
        intentId: 'production_open_wo_progress',
        reply: `Abriendo progreso de ${r.wo.id}.`,
        uiActions: [
          { type: 'navigate', to: `/production/work-orders/${r.displayId ?? r.wo.id}/progress` },
          { type: 'toast', level: 'info', text: `${r.wo.id} progreso (live).` },
        ],
      };
    },
  },
  {
    id: 'production_open_wo_docs',
    patterns: [
      /\b(?:documentos?|docs?|adjuntos?|attachments?|planos?|drawings?|fotos?|photos?|specs?|especificaciones?|reportes?\s+calidad|quality\s+reports?|instrucciones?\s+de\s+trabajo|work\s+instructions?)\b.*\b(?:WO|orden|order)/i,
      /\b(?:WO|orden|order)[-\s]?\d.*\b(?:documentos?|docs?|planos?|adjuntos?|fotos?|specs?|especificaciones?|reportes?\s+calidad|instrucciones?\s+de\s+trabajo|work\s+instructions?)/i,
      /\binstrucciones?\s+de\s+trabajo\s+WO/i,
      /\bwork\s+instructions?\s+WO/i,
      /\bspecs?\s+de\s+(?:la\s+)?(?:WO|orden|order)/i,
      /\b(?:WO|orden|order)\s+\d+\s+specs?\b/i,
    ],
    handler: (_m, msg) => {
      const r = resolveWorkOrder(msg);
      if (!r.wo) return { reply: 'No identifico la WO. Prueba "docs de WO-2026-0042".' };
      return {
        intentId: 'production_open_wo_docs',
        reply: `Abriendo documentos de ${r.wo.id}.`,
        uiActions: [
          { type: 'navigate', to: `/production/work-orders/${r.displayId ?? r.wo.id}/docs` },
          { type: 'toast', level: 'info', text: `Documentos de ${r.wo.id}.` },
        ],
      };
    },
  },
  {
    id: 'production_open_wo_certs',
    // L9.5 · MUST precede open_fai so "FAI orden 5" → /work-orders/.../certs (not generic /fai/dossier).
    patterns: [
      /\b(?:certificados?|certs?|certifications?|firmas?|signatures?|caducad|expir|COC|FAI|EASA|AS9100|RoHS|REACH)\b.*\b(?:WO|orden|order)/i,
      /\b(?:WO|orden|order)[-\s]?\d.*\b(?:certificados?|certs?|firmas?|caducad|expir|COC|FAI|RoHS|REACH)/i,
    ],
    handler: (_m, msg) => {
      const r = resolveWorkOrder(msg);
      if (!r.wo) return { reply: 'No identifico la WO. Prueba "certificados de WO-2026-0042".' };
      return {
        intentId: 'production_open_wo_certs',
        reply: `Abriendo certificados de ${r.wo.id}.`,
        uiActions: [
          { type: 'navigate', to: `/production/work-orders/${r.displayId ?? r.wo.id}/certs` },
          { type: 'toast', level: 'info', text: `Certificados de ${r.wo.id}.` },
        ],
      };
    },
  },
  // ============================================================
  // L9 — WO Analítica avanzada (cierre M2)
  // ============================================================
  {
    id: 'production_open_wo_variance',
    // L9.5 · MUST precede costs to capture "varianza coste WO-X" / "cost variance WO-X"
    // and "what happened WO-X" (which costs/timeline could otherwise grab).
    patterns: [
      // L9.5 Bloque 2.5 (audit fix): el catálogo asigna "varianza coste WO-X" / "cost variance WO-X" a COSTS,
      // y "what happened WO-X" a TIMELINE. Variance solo cubre los casos de root-cause genérico.
      /\b(?:varianzas?|variances?)\b(?!\s+(?:coste|cost))/i,
      /\bplan\s+(?:vs\.?|contra)\s+real\b/i,
      /\broot\s+cause\b/i,
      /\btop\s+variances?\b/i,
      // L9.5 · "qué pasó en WO-X" (con preposición "en/con") → variance (root cause).
      // "qué pasó WO-X" sin preposición y "what happened WO-X" se delegan a timeline (catálogo).
      /\bqu[ée]\s+pas[oó]\s+(?:en|con)\s+(?:la\s+)?(?:WO|orden|order)/i,
      // L9.5 · "comparar plan real WO-X" / "plan vs actual WO-X"
      /\bcomparar\s+plan\s+real\b/i,
      /\bplan\s+vs\.?\s+actual\b/i,
      // L9.5 · "varianzas por operación / variances per operation" / "variances by op"
      /\bvarianzas?\s+por\s+operaci[oó]n\b/i,
      /\bvariances?\s+(?:per|by)\s+op(?:eration)?\b/i,
    ],
    handler: (_m, msg) => {
      const r = resolveWorkOrder(msg);
      if (!r.wo) return { reply: 'No identifico la WO. Prueba "varianza de WO-2026-0046".' };
      const dim =
        /coste|cost/i.test(msg) ? 'cost'
        : /tiempo|time/i.test(msg) ? 'time'
        : /calidad|quality/i.test(msg) ? 'quality'
        : /cantidad|qty|quantity/i.test(msg) ? 'quantity'
        : undefined;
      const costPct = ((r.wo.costActual - r.wo.costPlanned) / Math.max(1, r.wo.costPlanned)) * 100;
      return {
        intentId: 'production_open_wo_variance',
        reply: `Abriendo varianza de ${r.wo.id}${dim ? ` · dim ${dim}` : ''}.`,
        uiActions: [
          { type: 'navigate', to: `/production/work-orders/${r.displayId ?? r.wo.id}/variance` },
          { type: 'toast', level: Math.abs(costPct) > 10 ? 'warning' : 'info',
            text: `${r.wo.id} · varianza coste ${costPct >= 0 ? '+' : ''}${costPct.toFixed(1)}%.` },
        ],
      };
    },
  },
  {
    id: 'production_open_wo_bottleneck',
    // L9.5 · MUST precede routing so "operaciones saturadas / saturated ops" → bottleneck.
    patterns: [
      /\b(?:bottleneck|cuello\s+de\s+botella|cuello|qu[ée]\s+limita|what\s+limits|eficiencia\s+l[íi]nea|line\s+efficiency)\b/i,
      /\boperaciones?\s+saturad[oa]s?\b/i,
      /\bsaturated\s+ops?\b/i,
    ],
    handler: (_m, msg) => {
      const r = resolveWorkOrder(msg);
      if (!r.wo) return { reply: 'No identifico la WO. Prueba "bottleneck WO-2026-0046".' };
      return {
        intentId: 'production_open_wo_bottleneck',
        reply: `Abriendo bottlenecks de ${r.wo.id}.`,
        uiActions: [
          { type: 'navigate', to: `/production/work-orders/${r.displayId ?? r.wo.id}/bottleneck` },
          { type: 'toast', level: 'warning', text: `${r.wo.id} · revisando cuellos de botella.` },
        ],
      };
    },
  },
  {
    id: 'production_open_wo_delay_risk',
    // L9.5 (audit fix) · "probabilidad on-time / on-time prob" moved to progress per catalog.
    patterns: [
      /\b(?:delay\s*risk|riesgo\s+(?:de\s+)?retraso|probabilidad\s+(?:de\s+)?retraso|delay\s+probability|escenarios?|scenarios?|mitigaci[oó]n\s+delay|delay\s+mitigation|wos?\s+(?:at\s+(?:critical\s+)?risk|en\s+riesgo(?:\s+cr[íi]tico)?))\b/i,
      /\bpredicci[oó]n\s+(?:de\s+)?(?:retraso|delay)\b/i,
      /\bdelay\s+prediction\b/i,
    ],
    handler: (_m, msg) => {
      const r = resolveWorkOrder(msg);
      const w = r.wo;
      // L9.5 · For list-mode "WOs en riesgo crítico" the catalog anchors on WO-2024-009.
      const display = r.displayId ?? w?.id ?? 'WO-2024-009';
      return {
        intentId: 'production_open_wo_delay_risk',
        reply: w ? `Abriendo predicción de retraso de ${w.id}.` : 'Abriendo vista de WOs en riesgo de retraso.',
        uiActions: [
          { type: 'navigate', to: `/production/work-orders/${display}/delay-risk` },
          { type: 'toast', level: 'info', text: w ? `${w.id} · análisis de riesgo de retraso.` : 'Análisis de riesgo de retraso (vista global).' },
        ],
      };
    },
  },
  {
    id: 'production_open_wo_detail',
    patterns: [
      /\b(?:abre|abrir|muéstrame|muestrame|ver|detalle\s+de|open|show)\b.*\b(?:WO|orden(?:\s+de\s+trabajo)?|work\s+order|order)\b/i,
      /\bWO-\d{4}(?:-\d{4})?\b/i,
      /\b(?:WO|orden(?:\s+de\s+trabajo)?|work\s+order|order)\s+(?:actual|en\s+curso|in[-\s]?progress|de\s+\w+|para\s+\w+|panel\s+solar|400W|450W|en\s+LINE|en\s+l[íi]nea)/i,
      /\b[úu]ltima\s+(?:WO\s+)?(?:terminad|complet)/i,
      /\bqu[ée]\s+pasa\s+con\s+(?:la\s+)?(?:orden|WO)/i,
      // L9.5 · "información WO-X" / "what about order N" / generic catch-alls
      /\binformaci[oó]n\s+(?:de\s+)?(?:la\s+)?(?:WO|orden|order)/i,
      /\bwhat\s+about\s+(?:WO|order)\s+\d/i,
      /\b(?:WO|orden|order)[-\s]?\d{1,4}\b/i,
    ],
    handler: (_m, msg) => {
      // resolveWorkOrder imported statically at top of file
      const r = resolveWorkOrder(msg);
      if (!r.wo) {
        return { reply: 'No he podido identificar la Work Order. Prueba con un ID (ej. WO-2026-0042), un cliente ("WO de Acme") o "WO actual".' };
      }
      const w = r.wo;
      const displayId = r.displayId ?? w.id;
      const pct = w.qtyPlanned > 0 ? Math.round((w.qtyProduced / w.qtyPlanned) * 100) : 0;
      const days = Math.ceil((new Date(w.dueDate).getTime() - Date.now()) / (24 * 3600 * 1000));
      return {
        intentId: 'production_open_wo_detail',
        reply: `Abriendo ${displayId} — ${w.customerName} · ${w.productDescription}.`,
        uiActions: [
          { type: 'navigate', to: `/production/work-orders/${displayId}` },
          { type: 'highlight', target: 'tab-resumen', duration: 1500 },
          { type: 'toast', level: 'info', text: `${displayId} (${w.customerName}) — ${w.qtyProduced}/${w.qtyPlanned} uds (${pct}%) · due en ${days}d.` },
        ],
        memoryPatch: { lastWorkOrder: w.id },
      };
    },
  },
  // ============================================================
  // L5 — Vistas sistémicas agregadas (Patrón F · cierre M1)
  // Sin :id en la ruta. Deben preceder a EDGE-METRICS / EDGE-DETAIL.
  // ============================================================
  // ---- L5 — DEVICE-NETWORK-TOPO ----
  // L9.5 Bloque 2.5: id canónico + path sin query strings (slots como set_filter UI-action).
  {
    id: 'devices_open_network_topology',
    patterns: [
      /\b(?:topolog[íi]a|topology|grafo|graph|network\s+map|mapa\s+de\s+red|diagrama\s+de\s+red|diagrama\s+red\s+planta|plant\s+network\s+diagram|red\s+de\s+(?:la\s+)?planta|red\s+de\s+la\s+l[íi]nea)\b/i,
      /\b(?:c[óo]mo\s+est[áa]n?\s+conectad[ao]s?|how\s+are\s+(?:the\s+)?devices\s+connected|c[óo]mo\s+se\s+conectan)\b/i,
      // L9.5 Bloque 2.5 · "mapa de conexiones línea B" / "line B connections map"
      /\bmapa\s+de\s+conexiones\b/i,
      /\bconnections?\s+map\b/i,
      // L9.5 Bloque 2.5 · "qué edge alimenta a CAM-X" / "what edge feeds CAM-X" — relación de red
      /\bqu[ée]\s+edge\s+alimenta\b/i,
      /\bwhat\s+edge\s+(?:feeds?|powers?|connects?\s+to)\b/i,
    ],
    handler: (_m, msg) => {
      const text = msg.toLowerCase();
      const focus =
        msg.match(/(CAM-\d+|EDGE-\d+|PLC-\d+)/i)?.[1].toUpperCase() ?? null;
      const lineM = text.match(/\b(?:l[íi]nea|line)\s+([a-d])/i);
      const setFilters: UIAction[] = [];
      if (focus) setFilters.push({ type: 'set_filter', view: 'network-topology', field: 'focus', value: focus });
      if (lineM) setFilters.push({ type: 'set_filter', view: 'network-topology', field: 'line', value: `MDC-${lineM[1].toUpperCase()}` });
      return {
        reply: focus
          ? `Topología con foco en ${focus}`
          : 'Topología de red de la flota',
        uiActions: [
          { type: 'navigate', to: '/backoffice/devices/network-topology' },
          ...setFilters,
        ],
      };
    },
  },
  // ---- L5 — DEVICE-FLOOR-MAP ----
  // L9.5 Bloque 2.5: id canónico + path sin query strings (focus = highlight UI-action).
  {
    id: 'devices_open_floor_map',
    patterns: [
      /\b(?:mapa\s+(?:de\s+)?(?:planta|f[áa]brica|f[áa]brico)|floor\s+map|floor\s+plan|plano\s+(?:de\s+)?(?:planta|f[áa]brica)|vista\s+de\s+planta|ubicaci[óo]n\s+f[íi]sica)\b/i,
      /\b(?:d[óo]nde\s+est[áa]|where\s+is)\s+(?:la\s+c[áa]mara\s+|el\s+|the\s+)?(CAM-\d+|EDGE-\d+|PLC-\d+)\b/i,
      /\bmu[eé]strame\s+el\s+mapa\b/i,
      /\bmapa\b[^\n]*\b(?:alertas?|detecciones?|throughput|dispositivos?)\b/i,
      /\bdispositivos?[^\n]*\ben\s+el\s+mapa\b/i,
      // L9.5 Bloque 2.5 · frases coloquiales del catálogo
      /\bheat\s*map\b/i,
      /\bmapa\s+de\s+calor\b/i,
      /\b(?:l[íi]nea|line)\s+[a-d]\s+devices?\s+on\s+map\b/i,
      /\bdispositivos?\s+(?:de\s+(?:la\s+)?)?l[íi]nea\s+[a-d]\s+(?:en\s+el\s+)?mapa\b/i,
      /\b(?:locate|locat[ae]r?)\s+(CAM-\d+|EDGE-\d+|PLC-\d+)\b/i,
      /\bubicaci[óo]n\s+(CAM-\d+|EDGE-\d+|PLC-\d+)\b/i,
      // L9.5 Bloque 2.5 · "EDGE-X physical location" / "map with active alerts"
      /\b(CAM-\d+|EDGE-\d+|PLC-\d+)\s+physical\s+location\b/i,
      /\b(CAM-\d+|EDGE-\d+|PLC-\d+)\s+ubicaci[óo]n\s+f[íi]sica\b/i,
      /\bmap\s+with\s+(?:active\s+)?alerts?\b/i,
      /\bmapa\s+con\s+alertas?\s+activas?\b/i,
      /\b(CAM-\d+|EDGE-\d+|PLC-\d+)\s+location\b/i,
    ],
    handler: (m, msg) => {
      const text = msg.toLowerCase();
      const focus =
        m[1]?.toUpperCase() ??
        msg.match(/(CAM-\d+|EDGE-\d+|PLC-\d+)/i)?.[1].toUpperCase() ??
        null;
      const lineM = text.match(/\b(?:l[íi]nea|line)\s+([a-d])/i);
      const setFilters: UIAction[] = [];
      if (lineM) setFilters.push({ type: 'set_filter', view: 'floor-map', field: 'line', value: `MDC-${lineM[1].toUpperCase()}` });
      // Heat overlay: still expose as set_filter so canonical path stays clean.
      if (/\balertas?\s+activas?|active\s+alerts?\b/i.test(text)) setFilters.push({ type: 'set_filter', view: 'floor-map', field: 'heat', value: 'alerts' });
      else if (/\bdetecciones?|detections?\b/i.test(text)) setFilters.push({ type: 'set_filter', view: 'floor-map', field: 'heat', value: 'detections' });
      else if (/\bthroughput|carga\b/i.test(text)) setFilters.push({ type: 'set_filter', view: 'floor-map', field: 'heat', value: 'throughput' });
      const actions: UIAction[] = [
        { type: 'navigate', to: '/backoffice/devices/floor-map' },
        ...setFilters,
      ];
      if (focus) {
        actions.push({ type: 'highlight', selector: `[data-device-id="${focus}"]`, durationMs: 2000 });
      }
      return {
        reply: focus
          ? `Mapa de planta · foco en ${focus}`
          : 'Mapa de planta con dispositivos georreferenciados',
        uiActions: actions,
      };
    },
  },
  // ---- L5 — DEVICE-SLA (multi-filtros: range + deviceType + line + breachOnly) ----
  {
    id: 'devices_open_sla',
    patterns: [
      /\b(?:sla|cumplimiento\s+sla|compliance\s+sla|uptime|downtime|mtbf|mttr|breach(?:es)?|incumplimiento)\b/i,
      /\bqu[ée]\s+dispositivos?\s+est[áa]n?\s+en\s+breach\b/i,
      /\breporte\s+de\s+sla\b/i,
    ],
    handler: (_m, msg) => {
      const text = msg.toLowerCase();
      const params = new URLSearchParams();

      // Range
      if (/(7\s*d|7\s*d[íi]as|last\s+week|[úu]ltima\s+semana)/i.test(text)) params.set('range', '7d');
      else if (/(mes\s+pasad[oa]|last\s+month|30\s*d|[úu]ltimo\s+mes)/i.test(text)) params.set('range', '30d');
      else if (/([úu]ltimo\s+trimestre|last\s+quarter|90\s*d|3\s*meses)/i.test(text)) params.set('range', '90d');
      else if (/(6\s*meses|6m|semestre|last\s+6\s+months|[úu]ltimos\s+6\s+meses)/i.test(text)) params.set('range', '6m');

      // Device type
      if (/\bc[áa]maras?|cameras?\b/.test(text)) params.set('type', 'camera');
      else if (/\bedges?|jetson\b/.test(text)) params.set('type', 'edge');
      else if (/\bplcs?\b/.test(text)) params.set('type', 'plc');

      // Line
      const lineM = text.match(/\bl[íi]nea\s+([a-d])|line\s+([a-d])/i);
      if (lineM) params.set('line', `MDC-${(lineM[1] ?? lineM[2] ?? '').toUpperCase()}`);

      // Breach only
      if (/\bbreach(?:es)?|incumplimiento|en\s+breach\b/.test(text)) params.set('breachOnly', 'true');

      const filters: string[] = [];
      const setFilters: UIAction[] = [];
      const rangeMap: Record<string, string> = { '7d': '7d', '30d': 'last_month', '90d': 'quarter', '6m': '6m' };
      if (params.get('range')) {
        const v = rangeMap[params.get('range')!] ?? params.get('range')!;
        filters.push(`rango ${v}`);
        setFilters.push({ type: 'set_filter', view: 'sla', field: 'range', value: v });
      }
      if (params.get('type')) {
        filters.push(params.get('type')!);
        setFilters.push({ type: 'set_filter', view: 'sla', field: 'deviceType', value: params.get('type')! });
      }
      if (params.get('line')) {
        filters.push(params.get('line')!);
        setFilters.push({ type: 'set_filter', view: 'sla', field: 'line', value: params.get('line')! });
      }
      if (params.get('breachOnly')) {
        filters.push('solo breach');
        setFilters.push({ type: 'set_filter', view: 'sla', field: 'breachOnly', value: true });
      }

      return {
        reply: filters.length > 0
          ? `SLA · ${filters.join(' · ')}`
          : 'Dashboard de cumplimiento SLA',
        uiActions: [
          { type: 'navigate', to: '/backoffice/devices/sla' },
          ...setFilters,
        ],
      };
    },
  },
  // ---- L4 — EDGE-METRICS (telemetría con rango temporal · Patrón D) — must precede open_edge_detail ----
  // L9.5 Bloque 2.5: rango → set_filter UI-action; añadidos patrones "throttling" y "uso GPU".
  {
    id: 'devices_open_edge_metrics',
    patterns: [
      /\b(?:m[ée]tricas?|metrics|telemetr[íi]a|telemetry|uso|consumo|rendimiento|recursos|resources|throttl(?:ing|e)?)\b[^\n]*\b(?:edge|jetson|orin|nodo)\b[^\n]*?(EDGE-\d+|\d{1,2})/i,
      /\b(?:edge|jetson|orin)\b[^\n]*?(EDGE-\d+|\d{1,2})\b[^\n]*\b(?:m[ée]tricas?|metrics|telemetr[íi]a|telemetry|gpu|cpu|recursos|resources|rendimiento|throttl|24h|7d|6h|1h|últimas?|last)/i,
      /\b(?:gpu|cpu)\b[^\n]*\b(?:edge|jetson|orin)\b[^\n]*?(EDGE-\d+|\d{1,2})/i,
      // L9.5 Bloque 2.5 · "Orin 2 last 24h" — Orin como sinónimo de jetson/edge
      /\b(?:jetson\s+)?orin\s+(\d{1,2})\s+(?:last|últimas?|24h|7d|6h|1h)/i,
      // L9.5 Bloque 2.5 · "throttling EDGE-02 6 horas" / "throttle EDGE-02 last 6h"
      /\bthrottl(?:ing|e)?\s+(EDGE-\d+|\d{1,2})/i,
      // L9.5 Bloque 2.5 · "uso GPU del Jetson 2" / "GPU usage Jetson 2"
      /\b(?:uso|usage)\s+(?:de\s+)?(?:gpu|cpu)\b[^\n]*\b(?:edge|jetson|nodo)\b[^\n]*?(EDGE-\d+|\d{1,2})?/i,
    ],
    handler: (m, msg) => {
      // Prefer explicit EDGE-XX over loose digit captures (e.g. "últimas 24")
      const explicit = msg.match(/EDGE-(\d+)/i);
      let id: string;
      if (explicit) id = `EDGE-${explicit[1].padStart(2, '0')}`;
      else {
        const near = msg.match(/(?:edge|jetson|nodo)[^\d]{0,12}(\d{1,2})/i);
        const raw = (near?.[1] ?? m[1] ?? '01').toString();
        id = raw.toUpperCase().includes('EDGE-')
          ? raw.toUpperCase()
          : `EDGE-${String(raw).padStart(2, '0')}`;
      }
      const text = msg.toLowerCase();
      let range: '1h' | '6h' | '24h' | '7d' | null = null;
      if (/\b7\s*d|7\s*d[íi]as|week|semana\b/.test(text)) range = '7d';
      else if (/\b24\s*h|últimas?\s+24|last\s+24|d[íi]a\b/.test(text)) range = '24h';
      else if (/\b6\s*h|6\s*horas?\b/.test(text)) range = '6h';
      else if (/\b1\s*h\b/.test(text)) range = '1h';
      const actions: UIAction[] = [
        { type: 'navigate', to: `/backoffice/devices/edge/${id}/metrics` },
        { type: 'highlight', selector: '[data-id="kpi-gpu"]', durationMs: 1500 },
      ];
      if (range) actions.push({ type: 'set_filter', view: 'edge-metrics', field: 'range', value: range });
      return {
        reply: range ? `Métricas ${id} · rango ${range}` : `Métricas ${id}`,
        uiActions: actions,
      };
    },
  },
  // ---- L4 — DEVICE-FIRMWARE (cross-entity polimórfica · Patrón E) — must precede open_edge_detail/open_plc_detail ----
  {
    id: 'devices_open_firmware',
    patterns: [
      /\b(?:firmware|versi[oó]n(?:es)?|version|update|actualizar?|rollback|ota)\b[^\n]*\b(?:c[áa]mara|camera|edge|jetson|plc)\b[^\n]*?([A-Z]{2,5}-\d+|\d{1,2})?/i,
      /\b(?:c[áa]mara|camera|edge|jetson|plc)\b[^\n]*?([A-Z]{2,5}-\d+|\d{1,2})\b[^\n]*\b(?:firmware|versi[oó]n(?:es)?|version|update|rollback|ota)/i,
    ],
    handler: (_m, msg) => {
      const text = msg.toLowerCase();
      let dt: 'cameras' | 'edge' | 'plc' = 'cameras';
      let id: string | null = null;

      if (/\bplc\b/.test(text)) {
        dt = 'plc';
        const x = msg.match(/PLC-(\d+)/i) ?? msg.match(/plc[^\d]{0,8}(\d{1,2})/i);
        if (x) id = `PLC-${x[1].padStart(2, '0')}`;
      } else if (/\b(edge|jetson)\b/.test(text)) {
        dt = 'edge';
        const x = msg.match(/EDGE-(\d+)/i) ?? msg.match(/(?:edge|jetson)[^\d]{0,12}(\d{1,2})/i);
        if (x) id = `EDGE-${x[1].padStart(2, '0')}`;
        // L9.5 Bloque 2.5 · "firmware del edge de la línea A" / "firmware edge line A"
        if (!id) {
          const lineM = text.match(/\b(?:l[íi]nea|line)\s+([a-d])/i);
          if (lineM) {
            const map: Record<string, string> = { A: 'EDGE-01', B: 'EDGE-02', C: 'EDGE-03', D: 'EDGE-03' };
            id = map[lineM[1].toUpperCase()] ?? null;
          }
        }
      } else {
        dt = 'cameras';
        const x = msg.match(/CAM-(\d+)/i) ?? msg.match(/(?:c[áa]mara|camera)[^\d]{0,8}(\d{1,2})/i);
        if (x) id = `CAM-${(x[1] ?? '01').padStart(2, '0')}`;
      }

      if (!id) return { reply: 'Indica el dispositivo (p. ej. "firmware cámara CAM-01", "firmware edge 2", "firmware PLC 3").' };

      const isRollback = /\brollback\b/.test(text);
      return {
        reply: `Firmware ${dt} ${id}${isRollback ? ' · rollback' : ''}`,
        uiActions: [
          { type: 'navigate', to: `/backoffice/devices/${dt}/${id}/firmware` },
          { type: 'toast', message: `Firmware · ${dt} ${id}`, level: 'info' },
        ],
      };
    },
  },
  // ---- L4 — PLC-DETAIL (nueva entidad) ----
  {
    id: 'devices_open_plc_detail',
    // L9.5 Bloque 2.5: añadidos PLC-A signals (letra→id), "estado del PLC de la línea B", "señales PLC".
    patterns: [
      /\b(?:abre|abrir|ver|muestra|mu[eé]strame|detalle\s+(?:de|del)|open|show)\b[^\n]*\bplc[^\n]*?(PLC-\d+|\d{1,2})/i,
      /\bplc[^\n]*?(PLC-\d+|\d{1,2})\b[^\n]*\b(?:tags?|signals?|status|estado|valores?|values?|modbus|opc|señales?)/i,
      /\b(?:modbus|opc[\s-]?ua)\b[^\n]*\bplc[^\n]*?(PLC-\d+|\d{1,2})/i,
      /\b(?:l[íi]nea|line)\s+[a-d]\s+plc\s+(?:status|tags?|signals?|estado|señales?)/i,
      /\bPLC-\d+\b/i,
      // L9.5 Bloque 2.5 · "PLC-A signals" / "PLC-B status" (letra mapeada a id)
      /\bPLC-([A-D])\b/i,
      // L9.5 Bloque 2.5 · "estado del PLC de la línea B" / "señales PLC línea A"
      /\b(?:estado|status|señales?|signals?|tags?)\s+(?:del?\s+)?plc\s+(?:de\s+(?:la\s+)?)?(?:l[íi]nea|line)\s+([a-d])/i,
      /\bplc\s+(?:de\s+(?:la\s+)?)?(?:l[íi]nea|line)\s+([a-d])/i,
    ],
    handler: (m, msg) => {
      let id: string | null = null;
      const text = msg.toLowerCase();
      const explicit = msg.match(/PLC-(\d+)/i);
      if (explicit) id = `PLC-${explicit[1].padStart(2, '0')}`;
      // L9.5 Bloque 2.5 · PLC-A → PLC-01, PLC-B → PLC-02, etc.
      if (!id) {
        const letter = msg.match(/\bPLC-([A-D])\b/i);
        if (letter) {
          const map: Record<string, string> = { A: '01', B: '02', C: '03', D: '03' };
          id = `PLC-${map[letter[1].toUpperCase()]}`;
        }
      }
      if (!id) {
        const numMatch = msg.match(/plc[^\d]{0,8}(\d{1,2})/i);
        if (numMatch) id = `PLC-${numMatch[1].padStart(2, '0')}`;
        else if (m[1] && /^\d+$/.test(String(m[1]))) id = `PLC-${String(m[1]).padStart(2, '0')}`;
      }
      if (!id) {
        const ln = text.match(/\b(?:l[íi]nea|line)\s+([a-d])/i);
        if (ln) {
          const map: Record<string, string> = { A: '01', B: '02', C: '03', D: '03' };
          id = `PLC-${map[ln[1].toUpperCase()] ?? '01'}`;
        }
      }
      if (!id) return { reply: 'Indica un PLC (p. ej. "abre PLC-01" o "PLC 2 tags").' };
      return {
        reply: `Abriendo ${id}`,
        uiActions: [{ type: 'navigate', to: `/backoffice/devices/plc/${id}` }],
      };
    },
  },
  // ---- L3 — EDGE-DETAIL (entidad relacionada · resolución por línea) ----
  {
    id: 'devices_open_edge_detail',
    patterns: [
      /\b(?:abre|abrir|ver|muestra|mu[eé]strame|detalle\s+del?|información|info)\b[^\n]*\b(?:edge|jetson|nodo\s+edge|edge\s+node)\b[^\n]*\b(EDGE-\d+|\d{1,2})/i,
      /\b(?:edge|jetson|nodo\s+edge|edge\s+node)\b[^\n]*\b(EDGE-\d+|\d{1,2})\b/i,
      /\bEDGE-\d+\s+(?:info|details?|detalle)\b/i,
      /\b(?:open|show)\s+edge\s+(EDGE-\d+|\d{1,2})/i,
      /\bjetson\s+orin\s+(\d{1,2})/i,
      /\b(?:edge|jetson)\s+de\s+la\s+l[íi]nea\s+([a-d])/i,
      /\bedge\s+for\s+line\s+([a-d])/i,
    ],
    handler: (m, msg) => {
      let id: string | null = null;
      const text = msg.toLowerCase();

      // Resolve by line if present
      const lineM = text.match(/\b(?:l[íi]nea|line)\s+([a-d])/i);
      if (lineM) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-require-imports
          const { edgeForLine } = require('@/data/generators/cameraHealthGenerator');
          id = edgeForLine(`L-MDC-${lineM[1].toUpperCase()}`)
            ?? edgeForLine(`L-VLL-${lineM[1].toUpperCase()}`);
        } catch {
          // ignore
        }
        // Fallback heuristic if helper missed
        if (!id) {
          const map: Record<string, string> = { A: 'EDGE-01', B: 'EDGE-02', C: 'EDGE-03' };
          id = map[lineM[1].toUpperCase()] ?? null;
        }
      }

      if (!id) {
        const explicit = msg.match(/EDGE-(\d+)/i);
        if (explicit) id = `EDGE-${explicit[1].padStart(2, '0')}`;
      }
      if (!id) {
        const captured = (m[1] ?? '').toString();
        if (captured) {
          id = captured.toUpperCase().includes('EDGE-')
            ? captured.toUpperCase()
            : `EDGE-${captured.padStart(2, '0')}`;
        }
      }
      if (!id) {
        return { reply: 'Indica un nodo edge (p. ej. "abre EDGE-01" o "edge de la línea A").' };
      }

      // Compute connected cameras for richer toast
      let camsLine = '';
      let hwLine = '';
      try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const { generateEdgeNode } = require('@/data/generators/cameraHealthGenerator');
        const e = generateEdgeNode(id);
        camsLine = e.cameras.length ? ` — ${e.cameras.length} cámaras conectadas: ${e.cameras.join(', ')}` : ' — sin cámaras asignadas';
        hwLine = ` (${e.hwModel})`;
      } catch {
        // ignore
      }

      return {
        reply: `${id}${hwLine}${camsLine}`,
        uiActions: [
          { type: 'navigate', to: `/backoffice/devices/edge/${id}` },
          { type: 'toast', message: `${id}${camsLine}`, level: 'info' },
        ],
      };
    },
  },
  {
    id: 'devices_open_camera_detail',
    patterns: [
      /\b(?:abre|abrir|ver|muestra|mu[eé]strame|mostrar|detalle|detalles?|info|informaci[óo]n|estado|qu[eé]\s+pasa\s+con|estaci[óo]n)\b[^\n]*\bc[áa]mara\b[^\n]*?(CAM-\d+|\d{1,2})\b/i,
      /\bc[áa]mara\b[^\n]*?(CAM-\d+|\d{1,2})\b[^\n]*\b(?:detalle|details?|info|status|estado|details)/i,
      /\b(?:open|show|view|details?|info|status|what'?s\s+up\s+with|at\s+station)\b[^\n]*\bcamera\b[^\n]*?(CAM-\d+|\d{1,2})\b/i,
      /\bcamera\b[^\n]*?(CAM-\d+|\d{1,2})\b[^\n]*\b(?:details?|info|status)/i,
      // L9.5 Bloque 2.5 · "cámara de la estación 3" / "camera at station 3" — keyword DESPUÉS de cámara
      /\bc[áa]mara\b[^\n]*\bestaci[óo]n\s+(\d{1,2})\b/i,
      /\bcamera\b[^\n]*\bat\s+station\s+(\d{1,2})\b/i,
      // L9.5 Bloque 2.5 · "camera N status" — explícito (audit fix: catálogo TR-007 → detail, no health/kpis)
      /\bcamera\s+(\d{1,2})\s+status\b/i,
    ],
    handler: (m, msg) => {
      const raw = (m[1] ?? '').toUpperCase();
      const id = raw.startsWith('CAM-') ? raw : `CAM-${String(raw).padStart(2, '0')}`;
      return {
        reply: `Abriendo cámara ${id}…`,
        uiActions: [
          { type: 'navigate', to: `/backoffice/devices/cameras/${id}` },
          { type: 'select_item', view: 'devices', id },
        ],
      };
    },
  },

  // ============================================================
  // B. WORK ORDER OPERATIONS
  // ============================================================
  {
    id: 'change_wo',
    patterns: [/\b(cambiar|switch|cambia|set)\b.*(WO-\d{4}(?:-\d{4})?)/i, /\borden\s+(WO-\d{4}(?:-\d{4})?)/i],
    handler: (_m, msg) => {
      const wo = extractWOId(msg);
      if (!wo) return { reply: 'No he detectado el código. Usa el formato WO-YYYY-NNNN.' };
      return {
        reply: [`Cambiado a la orden ${wo}.`, `Listo. ${wo} es ahora tu orden activa.`],
        uiActions: [{ type: 'set_active_wo', orderId: wo }],
        memoryPatch: { lastWorkOrder: wo, mentionedEntities: { wo } },
      };
    },
  },
  {
    id: 'show_order_detail',
    patterns: [/\b(detalle|detail|abre|open|show)\s+(de\s+)?(la\s+)?(orden\s+)?(WO-\d{4}(?:-\d{4})?)/i],
    handler: (_m, msg) => {
      const wo = extractWOId(msg);
      if (!wo) return { reply: 'No detecté el ID. Usa el formato WO-YYYY-NNNN.' };
      const progress = Math.floor(Math.random() * 50) + 35;
      return {
        reply: `Aquí tienes ${wo}:`,
        widgets: [{
          type: 'wo_card', id: wo,
          product: 'Panel lateral A320 · Ref. 7842-B',
          progress, dueAt: 'Hoy 14:00',
          status: progress > 60 ? 'EN CURSO' : 'PENDIENTE',
        }],
        uiActions: [
          { type: 'navigate', to: '/production/dispatch' },
          { type: 'select_item', view: 'dispatch_board', id: wo },
          { type: 'highlight', selector: `[data-id="${wo}"]`, durationMs: 2500 },
        ],
        memoryPatch: { lastWorkOrder: wo, mentionedEntities: { wo } },
      };
    },
  },

  // ============================================================
  // C. PRODUCTION filters
  // ============================================================
  {
    id: 'urgent_orders',
    patterns: [/\b(órdenes|ordenes|orders)\s+(urgentes|urgent|crítica|critica)/i, /\b(urgentes|urgent)\s+(órdenes|ordenes|orders)/i],
    handler: () => ({
      reply: 'Filtrando órdenes urgentes.',
      uiActions: [
        { type: 'navigate', to: '/production/dispatch' },
        { type: 'set_filter', view: 'dispatch_board', field: 'priorityFilter', value: 'URGENT' },
      ],
    }),
  },
  {
    id: 'filter_by_customer',
    patterns: [/\b(órdenes|ordenes|orders)\s+(de|for|del?\s+cliente)\s+([A-Za-z][\w\s.-]{1,30})/i],
    handler: (match) => {
      const customer = (match[3] ?? '').trim().split(/\s{2,}/)[0].slice(0, 30);
      if (!customer) return { reply: 'No detecté el cliente. Repite el nombre, por favor.' };
      return {
        reply: `Buscando órdenes que mencionen "${customer}".`,
        uiActions: [
          { type: 'navigate', to: '/production/dispatch' },
          { type: 'set_filter', view: 'dispatch_board', field: 'searchTerm', value: customer },
        ],
      };
    },
  },
  {
    id: 'wip_by_station',
    patterns: [/\bwip.*(estación|station)\s+([A-Z0-9-]{2,8})/i],
    handler: (match) => {
      const station = match[2]?.toUpperCase();
      return {
        reply: `WIP de estación ${station}.`,
        uiActions: [
          { type: 'navigate', to: '/production/wip' },
          { type: 'set_filter', view: 'wip_board', field: 'stationFilter', value: station },
        ],
      };
    },
  },

  // ============================================================
  // D. QUALITY
  // ============================================================
  {
    id: 'show_urgent_ncs',
    patterns: [/(ncs?|defect).*(crítica|critica|urgent)/i, /(crítica|critica|urgent).*(ncs?|defect)/i],
    handler: () => ({
      reply: '3 NCs críticas abiertas. Te dejo el detalle:',
      widgets: [
        {
          type: 'nc_list',
          items: [
            { id: 'NC-45', severity: 'CRITICAL', title: 'Scratch lateral panel A', line: 'Línea A', ageMin: 22 },
            { id: 'NC-44', severity: 'CRITICAL', title: 'Tolerancia fuera rango', line: 'Línea B', ageMin: 47 },
            { id: 'NC-42', severity: 'CRITICAL', title: 'Marca de tinta', line: 'Línea A', ageMin: 95 },
          ],
        },
        {
          type: 'action_buttons',
          items: [
            { label: 'Ver Quality Board', sendText: 'abre quality board' },
            { label: 'Filtrar línea A', sendText: 'NCs críticas línea A' },
          ],
        },
      ],
      uiActions: [
        { type: 'navigate', to: '/quality/board' },
        { type: 'set_filter', view: 'quality_board', field: 'severityFilter', value: 'CRITICAL' },
      ],
    }),
  },
  {
    id: 'ncs_pending',
    patterns: [/\b(nc|no\s*conformidad).*(pendiente|pending)\b/i],
    handler: () => ({
      reply: 'NCs pendientes de disposición.',
      uiActions: [
        { type: 'navigate', to: '/quality/board' },
        { type: 'set_filter', view: 'quality_board', field: 'dispositionFilter', value: 'PENDING' },
      ],
    }),
  },
  {
    id: 'ncs_rework',
    patterns: [/\b(rework|reproceso)\b/i],
    handler: () => ({
      reply: 'NCs en rework.',
      uiActions: [
        { type: 'navigate', to: '/quality/board' },
        { type: 'set_filter', view: 'quality_board', field: 'dispositionFilter', value: 'REWORK' },
      ],
    }),
  },
  {
    id: 'ncs_scrap',
    patterns: [/\b(scrap|merma|descarte)\b/i],
    handler: () => ({
      reply: 'NCs descartadas (SCRAP).',
      uiActions: [
        { type: 'navigate', to: '/quality/board' },
        { type: 'set_filter', view: 'quality_board', field: 'dispositionFilter', value: 'SCRAP' },
      ],
    }),
  },

  // ============================================================
  // E. SIMPLE NAVIGATION — Production
  // ============================================================
  {
    id: 'open_assembly',
    patterns: [/\b(ensamblaje|assembly)\b/i],
    handler: () => ({ reply: 'Abriendo verificación de ensamblaje.', uiActions: [{ type: 'navigate', to: '/production/assembly' }] }),
  },
  {
    id: 'open_cutting',
    patterns: [/\b(corte\s+guiado|cutting)\b/i],
    handler: () => ({ reply: 'Abriendo corte guiado.', uiActions: [{ type: 'navigate', to: '/production/cutting' }] }),
  },
  {
    id: 'open_cobotics',
    patterns: [/\b(cobot|cob[oó]tica|cobotics|robots\s+colaborativos)\b/i],
    handler: () => ({ reply: 'Abriendo cobótica.', uiActions: [{ type: 'navigate', to: '/production/cobotics' }] }),
  },
  {
    id: 'open_wip',
    patterns: [/\b(wip|trabajo\s+en\s+curso|work\s*in\s*progress)\b/i],
    handler: () => ({ reply: 'Abriendo WIP Board.', uiActions: [{ type: 'navigate', to: '/production/wip' }] }),
  },
  {
    id: 'open_dispatch',
    patterns: [/\b(dispatch|despacho|cola\s+de\s+órdenes|cola\s+de\s+ordenes)\b/i],
    handler: () => ({ reply: 'Abriendo dispatch board.', uiActions: [{ type: 'navigate', to: '/production/dispatch' }] }),
  },

  // ============================================================
  // F. SIMPLE NAVIGATION — Quality
  // ============================================================
  {
    id: 'open_quality_analytics',
    patterns: [/\b(analytics\s+calidad|calidad\s+analytics|fpy|pareto\s+defectos|quality\s+analytics)\b/i],
    handler: () => ({ reply: 'Abriendo analytics de calidad.', uiActions: [{ type: 'navigate', to: '/quality/analytics' }] }),
  },
  {
    id: 'open_quality_board',
    patterns: [/\b(quality\s+board|calidad|nc\s+board)\b/i],
    handler: () => ({ reply: 'Abriendo Quality Board.', uiActions: [{ type: 'navigate', to: '/quality/board' }] }),
  },
  {
    id: 'open_defect_detection',
    patterns: [/\b(detecci[oó]n\s+de\s+defectos|defect\s+detection|visi[oó]n\s+de\s+defectos)\b/i],
    handler: () => ({ reply: 'Abriendo detección de defectos.', uiActions: [{ type: 'navigate', to: '/quality/defect-detection' }] }),
  },
  {
    id: 'open_vision_3d',
    patterns: [/\b(visi[oó]n\s*3d|vision\s*3d|inspecci[oó]n\s*3d|gd&t|tolerancias)\b/i],
    handler: () => ({ reply: 'Abriendo visión 3D.', uiActions: [{ type: 'navigate', to: '/quality/vision-3d' }] }),
  },
  {
    id: 'open_packaging',
    patterns: [/\b(empaquetado|packaging)\b/i],
    handler: () => ({ reply: 'Abriendo verificación de empaquetado.', uiActions: [{ type: 'navigate', to: '/quality/packaging' }] }),
  },
  {
    id: 'open_calibration',
    patterns: [/\b(calibrar\s+c[áa]mara|camera\s+calibration|calibraci[oó]n)\b/i],
    handler: () => ({ reply: 'Abriendo calibración de cámara.', uiActions: [{ type: 'navigate', to: '/quality/calibration' }] }),
  },

  // ============================================================
  // G. SIMPLE NAVIGATION — Logistics
  // ============================================================
  {
    id: 'open_warehouse',
    patterns: [/\b(almac[eé]n|warehouse|mapa\s+de\s+racks)\b/i],
    handler: () => ({ reply: 'Abriendo mapa de almacén.', uiActions: [{ type: 'navigate', to: '/logistics/warehouse' }] }),
  },
  {
    id: 'open_inventory',
    patterns: [/\b(inventario|inventory|stock\s+de\s+materiales)\b/i],
    handler: () => ({ reply: 'Abriendo inventario.', uiActions: [{ type: 'navigate', to: '/materials/inventory' }] }),
  },
  {
    id: 'open_barcode',
    patterns: [/\b(barcode|c[oó]digo\s+de\s+barras|gs1)\b/i],
    handler: () => ({ reply: 'Abriendo análisis de código de barras.', uiActions: [{ type: 'navigate', to: '/materials/barcode' }] }),
  },
  {
    id: 'trace_entity',
    patterns: [/\b(traza|trazar|genealog[ií]a|trace)\s+(SN-\d{4,}|SSCC-\d{4,}|WO-\d{4}(?:-\d{4})?)/i],
    handler: (_m, msg) => {
      const m = msg.match(/(SN-\d{4,}|SSCC-\d{4,}|WO-\d{4}(?:-\d{4})?)/i);
      const id = m?.[0].toUpperCase();
      if (!id) return { reply: 'Dame un ID válido (SN-xxxx, SSCC-xxxx o WO-xxxx).' };
      return {
        reply: `Trazando ${id}…`,
        uiActions: [
          { type: 'navigate', to: '/traceability/search' },
          { type: 'set_search', view: 'traceability', value: id },
        ],
      };
    },
  },
  {
    id: 'open_traceability',
    patterns: [/\b(trazabilidad|traceability|genealog[ií]a)\b/i],
    handler: () => ({ reply: 'Abriendo búsqueda de trazabilidad.', uiActions: [{ type: 'navigate', to: '/traceability/search' }] }),
  },

  // ============================================================
  // H. SIMPLE NAVIGATION — Maintenance
  // ============================================================
  {
    id: 'open_predictive',
    patterns: [/\b(predictivo|predictive\s+maintenance|mantenimiento\s+predictivo|rul)\b/i],
    handler: () => ({ reply: 'Abriendo mantenimiento predictivo.', uiActions: [{ type: 'navigate', to: '/maintenance/predictive' }] }),
  },
  {
    id: 'open_condition',
    patterns: [/\b(condition\s+monitoring|monitor(izaci[oó]n)?\s+condici[oó]n|vibraciones|telemetr[ií]a)\b/i],
    handler: () => ({ reply: 'Abriendo condition monitoring.', uiActions: [{ type: 'navigate', to: '/maintenance/condition' }] }),
  },
  {
    id: 'open_safety',
    patterns: [/\b(safety|seguridad|epi|ehs)\b/i],
    handler: () => ({ reply: 'Abriendo safety compliance.', uiActions: [{ type: 'navigate', to: '/maintenance/safety' }] }),
  },
  {
    id: 'call_maintenance',
    patterns: [/\b(avisa|llama|llamar|notifica).*(mantenimiento)/i],
    handler: () => ({
      reply: 'Voy a notificar a mantenimiento. Necesito tu confirmación:',
      widgets: [{
        type: 'confirm',
        prompt: '¿Confirmas el aviso al equipo de mantenimiento?',
        confirmLabel: 'Sí, avisar', cancelLabel: 'Cancelar', token: 'call_maint',
      }],
    }),
  },

  // ============================================================
  // I. SIMPLE NAVIGATION — MLOps (admin+supervisor)
  // ============================================================
  {
    id: 'open_sam2',
    patterns: [/\b(sam2|anotaci[oó]n\s+asistida|sam\s*2\s*studio)\b/i],
    handler: () => ({ reply: 'Abriendo SAM2 Studio.', uiActions: [{ type: 'navigate', to: '/mlops/sam2' }] }),
  },
  {
    id: 'open_model_factory',
    patterns: [/\b(model\s+factory|f[aá]brica\s+de\s+modelos|registro\s+de\s+modelos)\b/i],
    handler: () => ({ reply: 'Abriendo Model Factory.', uiActions: [{ type: 'navigate', to: '/mlops/model-factory' }] }),
  },
  {
    id: 'open_drift',
    patterns: [/\b(drift|data\s+drift|concept\s+drift|modelo\s+degradado)\b/i],
    handler: () => ({ reply: 'Abriendo Drift Monitoring.', uiActions: [{ type: 'navigate', to: '/mlops/drift' }] }),
  },
  {
    id: 'open_zero_shot',
    patterns: [/\b(zero[-\s]?shot|clip|sin\s+entrenar)\b/i],
    handler: () => ({ reply: 'Abriendo Zero-Shot Explorer.', uiActions: [{ type: 'navigate', to: '/mlops/zero-shot' }] }),
  },
  {
    id: 'open_dataset_qa',
    patterns: [/\b(dataset\s+qa|curaci[oó]n\s+de\s+dataset|calidad\s+del\s+dataset)\b/i],
    handler: () => ({ reply: 'Abriendo Dataset Curation.', uiActions: [{ type: 'navigate', to: '/mlops/dataset-qa' }] }),
  },
  {
    id: 'open_vector_search',
    patterns: [/\b(vector\s+search|b[uú]squeda\s+sem[aá]ntica|imagenes?\s+similares)\b/i],
    handler: () => ({ reply: 'Abriendo Vector Search.', uiActions: [{ type: 'navigate', to: '/mlops/vector-search' }] }),
  },
  {
    id: 'open_experiments',
    patterns: [/\b(experimentos?|experiments?|experiment\s+tracking|comparar\s+experimentos|mlflow)\b/i],
    handler: () => ({ reply: 'Abriendo Experiment Comparison.', uiActions: [{ type: 'navigate', to: '/mlops/experiments' }] }),
  },
  {
    id: 'open_onnx',
    patterns: [/\b(onnx|quantiz(e|aci[oó]n)|exportar\s+modelo)\b/i],
    handler: () => ({ reply: 'Abriendo ONNX Pipeline.', uiActions: [{ type: 'navigate', to: '/mlops/onnx-pipeline' }] }),
  },

  // ============================================================
  // J. SIMPLE NAVIGATION — BackOffice (admin)
  // ============================================================
  {
    id: 'open_devices',
    patterns: [/\b(dispositivos|device\s+manager|c[aá]maras?\s+y\s+plcs?)\b/i],
    handler: () => ({ reply: 'Abriendo Device Manager.', uiActions: [{ type: 'navigate', to: '/backoffice/devices' }] }),
  },
  {
    id: 'open_rules_engine',
    patterns: [/\b(motor\s+de\s+reglas|rules\s+engine|reglas\s+if\s+then)\b/i],
    handler: () => ({ reply: 'Abriendo Rules Engine.', uiActions: [{ type: 'navigate', to: '/backoffice/rules' }] }),
  },
  {
    id: 'open_erp_sync',
    patterns: [/\b(erp\s*sync|sincronizaci[oó]n\s+erp|sap\s+sync)\b/i],
    handler: () => ({ reply: 'Abriendo ERP Sync.', uiActions: [{ type: 'navigate', to: '/backoffice/erp-sync' }] }),
  },
  {
    id: 'open_admin_panel',
    patterns: [/\b(admin\s+panel|gesti[oó]n\s+de\s+usuarios|panel\s+admin)\b/i],
    handler: () => ({ reply: 'Abriendo Admin Panel.', uiActions: [{ type: 'navigate', to: '/backoffice/admin' }] }),
  },
  {
    id: 'open_reports',
    patterns: [/\b(control\s+tower|reportes?\s+ejecutivos?|dashboard\s+kpis?|torre\s+de\s+control)\b/i],
    handler: () => ({ reply: 'Abriendo Control Tower.', uiActions: [{ type: 'navigate', to: '/analytics/reports' }] }),
  },
  {
    id: 'open_audit_log',
    patterns: [/\b(audit\s+log|auditor[ií]a|registro\s+de\s+acciones)\b/i],
    handler: () => ({ reply: 'Abriendo Audit Log.', uiActions: [{ type: 'navigate', to: '/system/audit-log' }] }),
  },

  // ============================================================
  // K. FAI
  // ============================================================
  {
    id: 'open_fai',
    patterns: [/\b(fai|dossier\s+fai|as9102|primera\s+pieza|first\s+article)\b/i],
    handler: () => ({ reply: 'Abriendo FAI Dossier.', uiActions: [{ type: 'navigate', to: '/fai/dossier' }] }),
  },

  // ============================================================
  // L. TRANSVERSALES — OEE, alerts, current WO, priorities, confirm callback, help, greet
  // ============================================================
  {
    id: 'oee_query',
    patterns: [/\b(oee|rendimiento|eficiencia)\b/i],
    handler: () => {
      const availability = Number(fakePercent(92, 97));
      const performance = Number(fakePercent(88, 95));
      const quality = Number(fakePercent(98.5, 99.9));
      const oee = Number(((availability * performance * quality) / 10000).toFixed(1));
      return {
        reply: `OEE actual Línea A: ${oee}%.`,
        widgets: [
          { type: 'oee_gauge', availability, performance, quality, oee, line: 'Línea A' },
        ],
      };
    },
  },
  {
    id: 'alert_status',
    patterns: [/\b(alerta|alarma|alarm|alert)s?\b/i],
    handler: () => {
      const unack = useMESStore.getState().alerts.filter((a) => !a.acknowledged).length;
      if (unack === 0) {
        return { reply: 'No hay alertas abiertas. Todo en verde.' };
      }
      return {
        reply: `${unack} alerta${unack === 1 ? '' : 's'} sin reconocer.`,
        widgets: [
          {
            type: 'kpi_grid',
            title: 'Alertas turno actual',
            items: [
              { label: 'Sin reconocer', value: String(unack), tone: unack > 2 ? 'danger' : 'warning' },
              { label: 'Críticas', value: String(Math.min(unack, 2)), tone: 'danger' },
              { label: 'Reconocidas', value: '12', tone: 'success', delta: '+3' },
              { label: 'Tiempo medio', value: '8m', tone: 'neutral' },
            ],
          },
        ],
      };
    },
  },
  {
    id: 'current_wo',
    patterns: [/\b(qué|que)\s+(estoy\s+produciendo|orden\s+activa|wo\s+activa)/i, /\borden\s+actual\b/i],
    handler: () => {
      const active = useMESStore.getState().activeOrderId;
      return { reply: `Tu orden activa es ${active}.` };
    },
  },
  {
    id: 'urgent_today',
    patterns: [/(más|mas)\s+urgente|prioridad(es)?|qué\s+es\s+lo\s+urgente|what.*urgent.*today/i],
    handler: () => ({
      reply: 'Tres prioridades para el turno:',
      widgets: [
        {
          type: 'kpi_grid', title: 'Resumen del turno',
          items: [
            { label: 'NCs críticas', value: '3', tone: 'danger', delta: '+1' },
            { label: 'WOs en curso', value: '7', tone: 'neutral' },
            { label: 'OEE actual', value: '87.2%', tone: 'success', delta: '+2.1' },
            { label: 'Alertas', value: '2', tone: 'warning' },
          ],
        },
        {
          type: 'action_buttons',
          items: [
            { label: 'Ver NC-45 crítica', sendText: 'muéstrame las NCs críticas', variant: 'default' },
            { label: 'Ver WO-2024-0900', sendText: 'detalle de WO-2024-0900' },
            { label: 'Avisar mantenimiento', sendText: 'avisa a mantenimiento' },
          ],
        },
      ],
    }),
  },
  {
    id: 'confirmed_callback',
    patterns: [/^__confirmed__:/],
    handler: (_m, msg) => {
      const token = msg.replace(/^__confirmed__:/, '');
      if (token === 'call_maint') {
        return {
          reply: 'Notificando a mantenimiento…',
          uiActions: [
            {
              type: 'execute_tool',
              tool_name: 'maintenance.call_team',
              params: { urgency: 'NORMAL', description: 'Aviso solicitado desde el agente' },
              idempotency_key: newIdempotencyKey(),
              success_message: 'Mantenimiento notificado',
              error_message: 'No se pudo notificar a mantenimiento',
            },
          ],
        };
      }
      if (token.startsWith('deploy_')) {
        const [, modelId, env] = token.split('_');
        // Sprint 1: model deploy todavía no está en TOOL_REGISTRY (Sprint 2/4 con Claude+MCP).
        // Mantenemos toast informativo y queda registrado en el chat.
        return {
          reply: `${modelId} marcado para despliegue → ${env}.`,
          uiActions: [{ type: 'toast', message: `${modelId} → ${env} (pendiente integración)`, level: 'info' }],
        };
      }
      if (token.startsWith('disp_')) {
        const [, ncId, disposition] = token.split('_');
        return {
          reply: `Disposicionando ${ncId} como ${disposition}…`,
          uiActions: [
            {
              type: 'execute_tool',
              tool_name: 'quality.dispose_nc',
              params: { nc_id: ncId, disposition },
              idempotency_key: newIdempotencyKey(),
              success_message: `${ncId} → ${disposition}`,
              error_message: `No se pudo disposicionar ${ncId}`,
            },
          ],
        };
      }
      if (token.startsWith('assign_')) {
        const [, wo, station] = token.split('_');
        return {
          reply: `Asignando ${wo} a ${station}…`,
          uiActions: [
            {
              type: 'execute_tool',
              tool_name: 'production.assign_wo',
              params: { wo_id: wo, station },
              idempotency_key: newIdempotencyKey(),
              success_message: `${wo} asignada a ${station}`,
              error_message: `No se pudo asignar ${wo}`,
            },
          ],
        };
      }
      if (token.startsWith('prio_')) {
        const [, wo, priority] = token.split('_');
        return {
          reply: `Cambiando prioridad de ${wo}…`,
          uiActions: [
            {
              type: 'execute_tool',
              tool_name: 'production.change_wo_priority',
              params: { wo_id: wo, priority },
              idempotency_key: newIdempotencyKey(),
              success_message: `Prioridad ${priority} aplicada a ${wo}`,
              error_message: `No se pudo cambiar prioridad de ${wo}`,
            },
          ],
        };
      }
      if (token.startsWith('stop_')) {
        // stop_<machine>_<minutes>
        const [, machine, minutes] = token.split('_');
        return {
          reply: `Programando parada de ${machine}…`,
          uiActions: [
            {
              type: 'execute_tool',
              tool_name: 'maintenance.schedule_stop',
              params: {
                machine_id: machine,
                estimated_minutes: Number(minutes) || 30,
                description: 'Parada programada desde el agente',
              },
              idempotency_key: newIdempotencyKey(),
              success_message: `Parada programada en ${machine} (${minutes} min)`,
              error_message: `No se pudo programar la parada en ${machine}`,
            },
          ],
        };
      }
      if (token.startsWith('createnc_')) {
        // createnc_<wo>_<severity>_<descSlug>
        const parts = token.split('_');
        const wo = parts[1];
        const severity = parts[2];
        const description = decodeURIComponent(parts.slice(3).join('_'));
        return {
          reply: `Creando NC en ${wo}…`,
          uiActions: [
            {
              type: 'execute_tool',
              tool_name: 'quality.create_nc',
              params: { wo_id: wo, severity, description },
              idempotency_key: newIdempotencyKey(),
              success_message: `NC creada en ${wo}`,
              error_message: `No se pudo crear la NC en ${wo}`,
            },
          ],
        };
      }
      return { reply: `Acción "${token}" ejecutada.` };
    },
  },
  {
    id: 'help',
    patterns: [/\b(ayuda|help|qué\s+puedes\s+hacer|que\s+puedes\s+hacer|capabilities|menu)\b/i],
    handler: () => ({
      reply:
        'Puedo ayudarte con:\n' +
        '• **Producción** · dashboard, dispatch, assembly, cutting, cobotics, WIP\n' +
        '• **Calidad** · quality board, defect detection, visión 3D, packaging, analytics, calibración\n' +
        '• **Logística** · almacén, inventario, barcode, trazabilidad\n' +
        '• **Mantenimiento** · predictivo, condition monitoring, safety, avisar equipo, programar parada\n' +
        '• **MLOps** · SAM2, model factory, drift, zero-shot, dataset QA, vector search, experimentos, ONNX\n' +
        '• **BackOffice** · dispositivos, rules, ERP sync, admin, reportes, audit log\n' +
        '• **FAI** · dossier AS9102\n\n' +
        'Ejemplos: *"NCs críticas"*, *"abre model factory"*, *"crear NC"*, *"traza SN-78234"*, *"avisa a mantenimiento"*.',
    }),
  },
  {
    id: 'show_dashboard',
    patterns: [/\b(dashboard|resumen\s+general|overview|inicio|home)\b/i],
    handler: () => ({
      reply: ['Llevándote al dashboard.', 'Volviendo al overview.'],
      uiActions: [{ type: 'navigate', to: '/' }],
    }),
  },
  {
    id: 'greet',
    patterns: [/^\s*(hola|buenas|buenos\s+d[ií]as|buenas\s+tardes|hi|hey|hello|qué\s+tal)\b/i],
    handler: () => ({
      reply: ['¿En qué te ayudo con la planta hoy?', 'Dime, ¿qué necesitas ver?', '¡Hola! ¿Por dónde empezamos?'],
    }),
  },
];

// ============================================================
// Fallback intent
// ============================================================
const fallbackIntent: IntentResponse = {
  reply: [
    "No estoy seguro de haber entendido. ¿Puedes reformular? También puedes escribir 'ayuda'.",
    'No tengo una respuesta clara para eso. Prueba con órdenes, calidad, inventario, trazabilidad...',
    'Mmm, no lo pillo. Escribe "ayuda" y te listo lo que sé hacer.',
  ],
  intentId: 'fallback',
};

// ============================================================
// Main classifier
// ============================================================
export function classifyAndRespond(message: string, memory: AgentMemory): IntentResponse {
  // Pre-intent: extract entities and merge into memory snapshot for handlers
  const entities = extractEntities(message);
  const mergedMemory: AgentMemory = {
    ...memory,
    lastWorkOrder: entities.wo ?? memory.lastWorkOrder,
    mentionedEntities: { ...memory.mentionedEntities, ...entities },
  };

  // Always-on memory patch from extracted entities
  const entityPatch: Partial<AgentMemory> | null =
    Object.keys(entities).length > 0
      ? {
          mentionedEntities: { ...memory.mentionedEntities, ...entities },
          ...(entities.wo ? { lastWorkOrder: entities.wo } : {}),
        }
      : null;

  // ----- Multi-turn flow handling -----
  // 1. If a flow is active, advance it (unless user explicitly switches via a NEW flow trigger).
  if (memory.pendingFlow) {
    const newTrigger = findFlowTrigger(message);
    if (newTrigger && newTrigger.id !== memory.pendingFlow.id) {
      const r = startFlow(newTrigger, message);
      return {
        reply: r.reply,
        widgets: r.widgets,
        uiActions: r.uiActions,
        memoryPatch: { ...(entityPatch ?? {}), ...(r.memoryPatch ?? {}) },
        intentId: `flow:${newTrigger.id}`,
      };
    }
    const r = advanceFlow(memory.pendingFlow, message);
    return {
      reply: r.reply,
      widgets: r.widgets,
      uiActions: r.uiActions,
      memoryPatch: { ...(entityPatch ?? {}), ...(r.memoryPatch ?? {}) },
      intentId: `flow:${memory.pendingFlow.id}`,
    };
  }

  // 2. Otherwise, see if a new flow should start
  const flowTrigger = findFlowTrigger(message);
  if (flowTrigger) {
    const r = startFlow(flowTrigger, message);
    return {
      reply: r.reply,
      widgets: r.widgets,
      uiActions: r.uiActions,
      memoryPatch: { ...(entityPatch ?? {}), ...(r.memoryPatch ?? {}) },
      intentId: `flow:${flowTrigger.id}`,
    };
  }

  for (const intent of intents) {
    for (const pattern of intent.patterns) {
      const match = message.match(pattern);
      if (match) {
        const response = intent.handler(match, message, mergedMemory);
        const mergedPatch: Partial<AgentMemory> | undefined =
          entityPatch || response.memoryPatch
            ? {
                ...(entityPatch ?? {}),
                ...(response.memoryPatch ?? {}),
                mentionedEntities: {
                  ...memory.mentionedEntities,
                  ...entities,
                  ...(response.memoryPatch?.mentionedEntities ?? {}),
                },
              }
            : undefined;
        return { ...response, memoryPatch: mergedPatch, intentId: intent.id };
      }
    }
  }
  return { ...fallbackIntent, memoryPatch: entityPatch ?? undefined };
}

// ============================================================
// Conversation summary helper (used later in Prompt 6)
// ============================================================
export function getConversationSummary(memory: AgentMemory, _lastMessages: Message[]): string {
  const parts: string[] = [];
  if (memory.lastWorkOrder) parts.push(`Orden activa: ${memory.lastWorkOrder}`);
  if (memory.lastFilter) {
    parts.push(`Último filtro: ${memory.lastFilter.field}=${String(memory.lastFilter.value)} en ${memory.lastFilter.view}`);
  }
  if (parts.length === 0) return 'Aún no hemos hablado de nada concreto. ¿Por dónde empezamos?';
  return parts.join('. ') + '.';
}

// Re-export for convenience
export { randomFrom };

// ============================================================
// Example prompts shown when chat is empty (used in Prompt 5)
// ============================================================
export const EXAMPLE_PROMPTS: string[] = [
  'Muéstrame las NCs críticas de hoy',
  '¿Qué es lo más urgente?',
  'Abre Model Factory',
  'Traza SN-78234',
  'Programar parada',
  'Abre el dossier FAI',
  'Órdenes urgentes',
  'Avisa a mantenimiento',
  'WIP de la estación ST-A1',
  'Desplegar modelo a producción',
];
